import { apiClient } from './api.js';

class LocationService {
  // Get all countries
  async getAllCountries() {
    console.log('getAllCountries called'); // Added log
    try {
      const response = await apiClient.get('/location/countries');
      console.log('API response for countries:', response); // Added log

      let countriesList = [];
      // Check if API response contains valid, array-formatted data
      if (response && response.data && Array.isArray(response.data)) {
        countriesList = response.data;
        console.log('Countries from API:', countriesList); // Added log
      } else if (response && response.data) {
        // Log if data is present but not an array (unexpected format)
        console.warn('API for countries returned data but not in expected array format. Original data:', response.data);
        // Optionally, attempt to wrap non-array data if it's a single object and makes sense for the use case
        // For now, we'll treat unexpected formats as leading to an empty list from the API.
      } else {
        // Log if response or response.data is missing or falsy
        console.warn('API for countries returned no data or an unexpected response structure.');
      }

      // If the processed list from API is empty, fall back to mock data
      if (countriesList.length > 0) {
        console.log('Returning countries from API:', countriesList); // Added log
        return countriesList; // Return the list directly
      } else {
        console.warn('Country list from API is empty or was malformed. Falling back to mock data.');
        const mockCountries = this.getMockCountries();
        console.log('Returning mock countries:', mockCountries.data); // Added log
        return mockCountries.data; // Access the data property from mock
      }

    } catch (error) {
      console.error('Error fetching countries from API:', error);
      console.warn('Falling back to mock country data due to API error.');
      const mockCountries = this.getMockCountries();
      console.log('Returning mock countries due to API error:', mockCountries.data); // Added log
      return mockCountries.data; // Access the data property from mock
    }
  }

  // Get states by country
  async getStatesByCountry(countryId) {
    if (!countryId) {
      console.warn('No country ID provided to getStatesByCountry');
      return { data: [] };
    }
    
    try {
      const response = await apiClient.get(`/location/countries/${countryId}/states`);
      // Ensure data is an array before returning
      if (response && response.data) {
        if (!Array.isArray(response.data)) {
          console.warn(`API returned non-array data for states of country ${countryId}, converting to array`);
          // Convert to array if it's an object or create empty array if null/undefined
          const dataArray = response.data ? [response.data] : [];
          return { data: dataArray };
        }
        // Return the response directly if data is already an array
        return response;
      }
      // If response exists but has no data property, return empty array
      console.warn(`API response missing data property for states of country ${countryId}`);
      return { data: [] };
    } catch (error) {
      console.error(`Error getting states for country ${countryId}:`, error);
      // Fallback to mock data if API fails
      return this.getMockStates(countryId);
    }
  }

  // Get cities by state
  async getCitiesByState(stateId) {
    if (!stateId) {
      console.warn('No state ID provided to getCitiesByState');
      return { data: [] };
    }
    
    try {
      const response = await apiClient.get(`/location/states/${stateId}/cities`);
      // Ensure data is an array before returning
      if (response && response.data) {
        if (!Array.isArray(response.data)) {
          console.warn(`API returned non-array data for cities of state ${stateId}, converting to array`);
          // Convert to array if it's an object or create empty array if null/undefined
          const dataArray = response.data ? [response.data] : [];
          return { data: dataArray };
        }
        // Return the response directly if data is already an array
        return response;
      }
      // If response exists but has no data property, return empty array
      console.warn(`API response missing data property for cities of state ${stateId}`);
      return { data: [] };
    } catch (error) {
      console.error(`Error getting cities for state ${stateId}:`, error);
      // Fallback to mock data if API fails
      return this.getMockCities(stateId);
    }
  }
  
  // Search for countries by search term
  async searchCountries(searchTerm) {
    if (!searchTerm) return { data: [] };
    
    try {
      const response = await apiClient.get(`/location/countries/search?searchTerm=${encodeURIComponent(searchTerm)}`);
      // Ensure data is an array before returning
      if (response && response.data) {
        if (!Array.isArray(response.data)) {
          console.warn(`API returned non-array data for countries search with term "${searchTerm}", converting to array`);
          // Convert to array if it's an object or create empty array if null/undefined
          const dataArray = response.data ? [response.data] : [];
          return { data: dataArray };
        }
        // Return the response directly if data is already an array
        return response;
      }
      // If response exists but has no data property, return empty array
      console.warn(`API response missing data property for countries search with term "${searchTerm}"`);
      return { data: [] };
    } catch (error) {
      console.error(`Error searching countries with term "${searchTerm}":`, error);
      // Fallback to mock data search
      return this.searchMockCountries(searchTerm);
    }
  }

  // Search for states by country ID and search term
  async searchStates(countryId, searchTerm) {
    if (!countryId || !searchTerm) return { data: [] };
    
    try {
      const response = await apiClient.get(`/location/countries/${countryId}/states/search?searchTerm=${encodeURIComponent(searchTerm)}`);
      // Ensure data is an array before returning
      if (response && response.data) {
        if (!Array.isArray(response.data)) {
          console.warn(`API returned non-array data for states search with term "${searchTerm}", converting to array`);
          // Convert to array if it's an object or create empty array if null/undefined
          const dataArray = response.data ? [response.data] : [];
          return { data: dataArray };
        }
        // Return the response directly if data is already an array
        return response;
      }
      // If response exists but has no data property, return empty array
      console.warn(`API response missing data property for states search with term "${searchTerm}"`);
      return { data: [] };
    } catch (error) {
      console.error(`Error searching states for country ${countryId} with term "${searchTerm}":`, error);
      // Fallback to mock data search
      return this.searchMockStates(countryId, searchTerm);
    }
  }

  // Search for cities by state ID and search term
  async searchCities(stateId, searchTerm) {
    if (!stateId || !searchTerm) return { data: [] };
    
    try {
      const response = await apiClient.get(`/location/states/${stateId}/cities/search?searchTerm=${encodeURIComponent(searchTerm)}`);
      // Ensure data is an array before returning
      if (response && response.data) {
        if (!Array.isArray(response.data)) {
          console.warn(`API returned non-array data for cities search with term "${searchTerm}", converting to array`);
          // Convert to array if it's an object or create empty array if null/undefined
          const dataArray = response.data ? [response.data] : [];
          return { data: dataArray };
        }
        // Return the response directly if data is already an array
        return response;
      }
      // If response exists but has no data property, return empty array
      console.warn(`API response missing data property for cities search with term "${searchTerm}"`);
      return { data: [] };
    } catch (error) {
      console.error(`Error searching cities for state ${stateId} with term "${searchTerm}":`, error);
      // Fallback to mock data search
      return this.searchMockCities(stateId, searchTerm);
    }
  }
  
  // Search for locations by query string
  // This method can be used to support autocomplete functionality
  async searchLocations(type, query) {
    if (!query) return { data: [] };
    
    try {
      let results = [];
      
      switch (type) {
        case 'country':
          // Use the dedicated search endpoint for countries
          const response = await this.searchCountries(query);
          return response;
          
        case 'state':
          if (query.countryId && query.text) {
            // Use the dedicated search endpoint for states
            const response = await this.searchStates(query.countryId, query.text);
            return response;
          }
          break;
          
        case 'city':
          if (query.stateId && query.text) {
            // Use the dedicated search endpoint for cities
            const response = await this.searchCities(query.stateId, query.text);
            return response;
          }
          break;
          
        default:
          break;
      }
      
      // Ensure results is always an array
      if (!Array.isArray(results)) {
        console.warn(`Non-array results in searchLocations for ${type}, converting to array`);
        results = results ? [results] : [];
      }
      
      return { data: results };
    } catch (error) {
      console.error(`Error searching for ${type} with query "${query}":`, error);
      // Fallback to mock data search
      return this.searchMockLocations(type, query);
    }
  }
  
  // Helper method to search mock countries when API is not available
  searchMockCountries(searchTerm) {
    if (!searchTerm) return { data: [] };
    
    const queryLower = searchTerm.toLowerCase();
    const mockCountries = this.getMockCountries();
    const results = mockCountries.data.filter(country =>
      country.name.toLowerCase().includes(queryLower));
    
    return { data: results };
  }
  
  // Helper method to search mock states when API is not available
  searchMockStates(countryId, searchTerm) {
    if (!countryId || !searchTerm) return { data: [] };
    
    const queryLower = searchTerm.toLowerCase();
    const mockStates = this.getMockStates(countryId);
    const results = mockStates.data.filter(state =>
      state.name.toLowerCase().includes(queryLower));
    
    return { data: results };
  }
  
  // Helper method to search mock cities when API is not available
  searchMockCities(stateId, searchTerm) {
    if (!stateId || !searchTerm) return { data: [] };
    
    const queryLower = searchTerm.toLowerCase();
    const mockCities = this.getMockCities(stateId);
    const results = mockCities.data.filter(city =>
      city.name.toLowerCase().includes(queryLower));
    
    return { data: results };
  }
  
  // Helper method to search mock data when API is not available
  searchMockLocations(type, query) {
    if (!query) return { data: [] };

    
    let queryLower = '';
    let results = [];
    
    // Handle different query formats
    if (typeof query === 'string') {
      queryLower = query.toLowerCase();
    } else if (query.text) {
      queryLower = query.text.toLowerCase();
    } else {
      console.warn('Invalid query format for searchMockLocations:', query);
      return { data: [] };
    }
    
    switch (type) {
      case 'country':
        const mockCountries = this.getMockCountries();
        if (mockCountries && mockCountries.data) {
          results = mockCountries.data.filter(country => 
            country.name.toLowerCase().includes(queryLower));
        }
        break;
      case 'state':
        // Combine all states from all countries
        const statesByCountry = {
          1: this.getMockStates(1).data,
          2: this.getMockStates(2).data,
          3: this.getMockStates(3).data,
          4: this.getMockStates(4).data,
          5: this.getMockStates(5).data,
          6: this.getMockStates(6).data,
          7: this.getMockStates(7).data,
          8: this.getMockStates(8).data,
          9: this.getMockStates(9).data,
          10: this.getMockStates(10).data,
          11: this.getMockStates(11).data,
          12: this.getMockStates(12).data,
          13: this.getMockStates(13).data,
          14: this.getMockStates(14).data,
          15: this.getMockStates(15).data,
          16: this.getMockStates(16).data,
          17: this.getMockStates(17).data,
          18: this.getMockStates(18).data,
          19: this.getMockStates(19).data,
          20: this.getMockStates(20).data,
          21: this.getMockStates(21).data,
          22: this.getMockStates(22).data,
          23: this.getMockStates(23).data,
          24: this.getMockStates(24).data,
          25: this.getMockStates(25).data,
          26: this.getMockStates(26).data,
          27: this.getMockStates(27).data,
          28: this.getMockStates(28).data,
          29: this.getMockStates(29).data,
          30: this.getMockStates(30).data
        };
        
        // Flatten all state arrays into a single array
        Object.values(statesByCountry).forEach(states => {
          if (Array.isArray(states)) {
            results = [...results, ...states.filter(state => 
              state.name.toLowerCase().includes(queryLower))];
          }
        });
        break;
      case 'city':
        // If we have a specific stateId in the query object, only search that state
        if (query.stateId) {
          const cities = this.getMockCities(query.stateId).data;
          if (Array.isArray(cities)) {
            results = cities.filter(city => 
              city.name.toLowerCase().includes(queryLower));
          }
        } else {
          // Otherwise search all states
          const stateIds = [
            // Malaysia
            101, 102, 103, 104, 105, 106, 107, 108, 109, 110, 111, 112, 113, 114, 115,
            // Singapore
            201, 202, 203, 204, 205,
            // United States
            301, 302, 303, 304, 305, 306, 307, 308, 309, 310, 311, 312, 313, 314, 315, 316, 317, 318, 319, 320,
            // United Kingdom
            401, 402, 403, 404,
            // Australia
            501, 502, 503, 504, 505, 506, 507, 508,
            // Canada
            601, 602, 603, 604, 605, 606, 607, 608, 609, 610,
            // China
            701, 702, 703, 704, 705, 706, 707, 708,
            // India
            801, 802, 803, 804, 805, 806, 807, 808,
            // Japan
            901, 902, 903, 904, 905, 906,
            // Germany
            1001, 1002, 1003, 1004, 1005, 1006,
            // France
            1101, 1102, 1103, 1104, 1105,
            // Italy
            1201, 1202, 1203, 1204, 1205,
            // Brazil
            1301, 1302, 1303, 1304, 1305,
            // South Korea
            1401, 1402, 1403,
            // Indonesia
            1501, 1502, 1503,
            // Thailand
            1601, 1602, 1603,
            // Vietnam
            1701, 1702, 1703,
            // Philippines
            1801, 1802, 1803,
            // Spain
            1901, 1902, 1903,
            // Netherlands
            2001, 2002, 2003,
            // Mexico
            2101, 2102, 2103,
            // Turkey
            2201, 2202, 2203,
            // Switzerland
            2301, 2302, 2303,
            // Sweden
            2401, 2402, 2403,
            // New Zealand
            2501, 2502, 2503,
            // Ireland
            2601, 2602, 2603,
            // Belgium
            2701, 2702, 2703,
            // Norway
            2801, 2802, 2803,
            // Denmark
            2901, 2902, 2903,
            // Finland
            3001, 3002, 3003
          ]; // All state IDs that have cities
          
          stateIds.forEach(stateId => {
            const mockCities = this.getMockCities(stateId);
            if (mockCities && mockCities.data && Array.isArray(mockCities.data)) {
              results = [...results, ...mockCities.data.filter(city => 
                city.name.toLowerCase().includes(queryLower))];
            }
          });
        }
        break;
      default:
        break;
    }
    
    // Limit results to prevent performance issues with large datasets
    if (results.length > 100) {
      results = results.slice(0, 100);
    }
    
    return { data: results };
  }

  // For development/testing - returns mock data when API is not available
  getMockCountries() {
    return {
      data: [
        { id: 1, name: 'Malaysia', code: 'MY' },
        { id: 2, name: 'Singapore', code: 'SG' },
        { id: 3, name: 'United States', code: 'US' },
        { id: 4, name: 'United Kingdom', code: 'GB' },
        { id: 5, name: 'Australia', code: 'AU' },
        { id: 6, name: 'Canada', code: 'CA' },
        { id: 7, name: 'China', code: 'CN' },
        { id: 8, name: 'India', code: 'IN' },
        { id: 9, name: 'Japan', code: 'JP' },
        { id: 10, name: 'Germany', code: 'DE' },
        { id: 11, name: 'France', code: 'FR' },
        { id: 12, name: 'Italy', code: 'IT' },
        { id: 13, name: 'Brazil', code: 'BR' },
        { id: 14, name: 'South Korea', code: 'KR' },
        { id: 15, name: 'Indonesia', code: 'ID' },
        { id: 16, name: 'Thailand', code: 'TH' },
        { id: 17, name: 'Vietnam', code: 'VN' },
        { id: 18, name: 'Philippines', code: 'PH' },
        { id: 19, name: 'Spain', code: 'ES' },
        { id: 20, name: 'Netherlands', code: 'NL' },
        { id: 21, name: 'Mexico', code: 'MX' },
        { id: 22, name: 'Turkey', code: 'TR' },
        { id: 23, name: 'Switzerland', code: 'CH' },
        { id: 24, name: 'Sweden', code: 'SE' },
        { id: 25, name: 'New Zealand', code: 'NZ' },
        { id: 26, name: 'Ireland', code: 'IE' },
        { id: 27, name: 'Belgium', code: 'BE' },
        { id: 28, name: 'Norway', code: 'NO' },
        { id: 29, name: 'Denmark', code: 'DK' },
        { id: 30, name: 'Finland', code: 'FI' }
      ]
    };
  }
  
  getMockStates(countryId) {
    const statesByCountry = {
      1: [ // Malaysia
        { id: 101, name: 'Selangor', countryId: 1 },
        { id: 102, name: 'Kuala Lumpur', countryId: 1 },
        { id: 103, name: 'Penang', countryId: 1 },
        { id: 104, name: 'Johor', countryId: 1 },
        { id: 105, name: 'Sabah', countryId: 1 },
        { id: 106, name: 'Sarawak', countryId: 1 },
        { id: 107, name: 'Perak', countryId: 1 },
        { id: 108, name: 'Kedah', countryId: 1 },
        { id: 109, name: 'Kelantan', countryId: 1 },
        { id: 110, name: 'Melaka', countryId: 1 },
        { id: 111, name: 'Negeri Sembilan', countryId: 1 },
        { id: 112, name: 'Pahang', countryId: 1 },
        { id: 113, name: 'Terengganu', countryId: 1 },
        { id: 114, name: 'Perlis', countryId: 1 },
        { id: 115, name: 'Labuan', countryId: 1 }
      ],
      2: [ // Singapore
        { id: 201, name: 'Central Region', countryId: 2 },
        { id: 202, name: 'East Region', countryId: 2 },
        { id: 203, name: 'North Region', countryId: 2 },
        { id: 204, name: 'North-East Region', countryId: 2 },
        { id: 205, name: 'West Region', countryId: 2 }
      ],
      3: [ // United States
        { id: 301, name: 'California', countryId: 3 },
        { id: 302, name: 'New York', countryId: 3 },
        { id: 303, name: 'Texas', countryId: 3 },
        { id: 304, name: 'Florida', countryId: 3 },
        { id: 305, name: 'Illinois', countryId: 3 },
        { id: 306, name: 'Pennsylvania', countryId: 3 },
        { id: 307, name: 'Ohio', countryId: 3 },
        { id: 308, name: 'Georgia', countryId: 3 },
        { id: 309, name: 'North Carolina', countryId: 3 },
        { id: 310, name: 'Michigan', countryId: 3 },
        { id: 311, name: 'New Jersey', countryId: 3 },
        { id: 312, name: 'Virginia', countryId: 3 },
        { id: 313, name: 'Washington', countryId: 3 },
        { id: 314, name: 'Massachusetts', countryId: 3 },
        { id: 315, name: 'Arizona', countryId: 3 },
        { id: 316, name: 'Tennessee', countryId: 3 },
        { id: 317, name: 'Indiana', countryId: 3 },
        { id: 318, name: 'Missouri', countryId: 3 },
        { id: 319, name: 'Maryland', countryId: 3 },
        { id: 320, name: 'Wisconsin', countryId: 3 }
      ],
      4: [ // United Kingdom
        { id: 401, name: 'England', countryId: 4 },
        { id: 402, name: 'Scotland', countryId: 4 },
        { id: 403, name: 'Wales', countryId: 4 },
        { id: 404, name: 'Northern Ireland', countryId: 4 }
      ],
      5: [ // Australia
        { id: 501, name: 'New South Wales', countryId: 5 },
        { id: 502, name: 'Victoria', countryId: 5 },
        { id: 503, name: 'Queensland', countryId: 5 },
        { id: 504, name: 'Western Australia', countryId: 5 },
        { id: 505, name: 'South Australia', countryId: 5 },
        { id: 506, name: 'Tasmania', countryId: 5 },
        { id: 507, name: 'Australian Capital Territory', countryId: 5 },
        { id: 508, name: 'Northern Territory', countryId: 5 }
      ],
      6: [ // Canada
        { id: 601, name: 'Ontario', countryId: 6 },
        { id: 602, name: 'Quebec', countryId: 6 },
        { id: 603, name: 'British Columbia', countryId: 6 },
        { id: 604, name: 'Alberta', countryId: 6 },
        { id: 605, name: 'Manitoba', countryId: 6 },
        { id: 606, name: 'Saskatchewan', countryId: 6 },
        { id: 607, name: 'Nova Scotia', countryId: 6 },
        { id: 608, name: 'New Brunswick', countryId: 6 },
        { id: 609, name: 'Newfoundland and Labrador', countryId: 6 },
        { id: 610, name: 'Prince Edward Island', countryId: 6 }
      ],
      7: [ // China
        { id: 701, name: 'Beijing', countryId: 7 },
        { id: 702, name: 'Shanghai', countryId: 7 },
        { id: 703, name: 'Guangdong', countryId: 7 },
        { id: 704, name: 'Sichuan', countryId: 7 },
        { id: 705, name: 'Jiangsu', countryId: 7 },
        { id: 706, name: 'Zhejiang', countryId: 7 },
        { id: 707, name: 'Shandong', countryId: 7 },
        { id: 708, name: 'Hong Kong', countryId: 7 }
      ],
      8: [ // India
        { id: 801, name: 'Maharashtra', countryId: 8 },
        { id: 802, name: 'Delhi', countryId: 8 },
        { id: 803, name: 'Karnataka', countryId: 8 },
        { id: 804, name: 'Tamil Nadu', countryId: 8 },
        { id: 805, name: 'Uttar Pradesh', countryId: 8 },
        { id: 806, name: 'Gujarat', countryId: 8 },
        { id: 807, name: 'West Bengal', countryId: 8 },
        { id: 808, name: 'Telangana', countryId: 8 }
      ],
      9: [ // Japan
        { id: 901, name: 'Tokyo', countryId: 9 },
        { id: 902, name: 'Osaka', countryId: 9 },
        { id: 903, name: 'Kyoto', countryId: 9 },
        { id: 904, name: 'Hokkaido', countryId: 9 },
        { id: 905, name: 'Kanagawa', countryId: 9 },
        { id: 906, name: 'Aichi', countryId: 9 }
      ],
      10: [ // Germany
        { id: 1001, name: 'Berlin', countryId: 10 },
        { id: 1002, name: 'Bavaria', countryId: 10 },
        { id: 1003, name: 'North Rhine-Westphalia', countryId: 10 },
        { id: 1004, name: 'Baden-Württemberg', countryId: 10 },
        { id: 1005, name: 'Hesse', countryId: 10 },
        { id: 1006, name: 'Hamburg', countryId: 10 }
      ],
      11: [ // France
        { id: 1101, name: 'Île-de-France', countryId: 11 },
        { id: 1102, name: 'Provence-Alpes-Côte d\'Azur', countryId: 11 },
        { id: 1103, name: 'Auvergne-Rhône-Alpes', countryId: 11 },
        { id: 1104, name: 'Nouvelle-Aquitaine', countryId: 11 },
        { id: 1105, name: 'Occitanie', countryId: 11 }
      ],
      12: [ // Italy
        { id: 1201, name: 'Lazio', countryId: 12 },
        { id: 1202, name: 'Lombardy', countryId: 12 },
        { id: 1203, name: 'Campania', countryId: 12 },
        { id: 1204, name: 'Veneto', countryId: 12 },
        { id: 1205, name: 'Sicily', countryId: 12 }
      ],
      13: [ // Brazil
        { id: 1301, name: 'São Paulo', countryId: 13 },
        { id: 1302, name: 'Rio de Janeiro', countryId: 13 },
        { id: 1303, name: 'Minas Gerais', countryId: 13 },
        { id: 1304, name: 'Bahia', countryId: 13 },
        { id: 1305, name: 'Paraná', countryId: 13 }
      ],
      14: [ // South Korea
        { id: 1401, name: 'Seoul', countryId: 14 },
        { id: 1402, name: 'Busan', countryId: 14 },
        { id: 1403, name: 'Incheon', countryId: 14 }
      ],
      15: [ // Indonesia
        { id: 1501, name: 'Jakarta', countryId: 15 },
        { id: 1502, name: 'Bali', countryId: 15 },
        { id: 1503, name: 'West Java', countryId: 15 }
      ],
      16: [ // Thailand
        { id: 1601, name: 'Bangkok', countryId: 16 },
        { id: 1602, name: 'Chiang Mai', countryId: 16 },
        { id: 1603, name: 'Phuket', countryId: 16 }
      ],
      17: [ // Vietnam
        { id: 1701, name: 'Hanoi', countryId: 17 },
        { id: 1702, name: 'Ho Chi Minh City', countryId: 17 },
        { id: 1703, name: 'Da Nang', countryId: 17 }
      ],
      18: [ // Philippines
        { id: 1801, name: 'Metro Manila', countryId: 18 },
        { id: 1802, name: 'Cebu', countryId: 18 },
        { id: 1803, name: 'Davao', countryId: 18 }
      ],
      19: [ // Spain
        { id: 1901, name: 'Madrid', countryId: 19 },
        { id: 1902, name: 'Catalonia', countryId: 19 },
        { id: 1903, name: 'Andalusia', countryId: 19 }
      ],
      20: [ // Netherlands
        { id: 2001, name: 'North Holland', countryId: 20 },
        { id: 2002, name: 'South Holland', countryId: 20 },
        { id: 2003, name: 'Utrecht', countryId: 20 }
      ],
      21: [ // Mexico
        { id: 2101, name: 'Mexico City', countryId: 21 },
        { id: 2102, name: 'Jalisco', countryId: 21 },
        { id: 2103, name: 'Nuevo León', countryId: 21 }
      ],
      22: [ // Turkey
        { id: 2201, name: 'Istanbul', countryId: 22 },
        { id: 2202, name: 'Ankara', countryId: 22 },
        { id: 2203, name: 'Izmir', countryId: 22 }
      ],
      23: [ // Switzerland
        { id: 2301, name: 'Zurich', countryId: 23 },
        { id: 2302, name: 'Geneva', countryId: 23 },
        { id: 2303, name: 'Bern', countryId: 23 }
      ],
      24: [ // Sweden
        { id: 2401, name: 'Stockholm', countryId: 24 },
        { id: 2402, name: 'Västra Götaland', countryId: 24 },
        { id: 2403, name: 'Skåne', countryId: 24 }
      ],
      25: [ // New Zealand
        { id: 2501, name: 'Auckland', countryId: 25 },
        { id: 2502, name: 'Wellington', countryId: 25 },
        { id: 2503, name: 'Canterbury', countryId: 25 }
      ],
      26: [ // Ireland
        { id: 2601, name: 'Dublin', countryId: 26 },
        { id: 2602, name: 'Cork', countryId: 26 },
        { id: 2603, name: 'Galway', countryId: 26 }
      ],
      27: [ // Belgium
        { id: 2701, name: 'Brussels', countryId: 27 },
        { id: 2702, name: 'Flanders', countryId: 27 },
        { id: 2703, name: 'Wallonia', countryId: 27 }
      ],
      28: [ // Norway
        { id: 2801, name: 'Oslo', countryId: 28 },
        { id: 2802, name: 'Viken', countryId: 28 },
        { id: 2803, name: 'Vestland', countryId: 28 }
      ],
      29: [ // Denmark
        { id: 2901, name: 'Capital Region', countryId: 29 },
        { id: 2902, name: 'Central Denmark', countryId: 29 },
        { id: 2903, name: 'Southern Denmark', countryId: 29 }
      ],
      30: [ // Finland
        { id: 3001, name: 'Uusimaa', countryId: 30 },
        { id: 3002, name: 'Pirkanmaa', countryId: 30 },
        { id: 3003, name: 'Southwest Finland', countryId: 30 }
      ]
    };
    
    return { data: statesByCountry[countryId] || [] };
  }
  
  // Get mock cities by state ID
  getMockCities(stateId) {
    if (!stateId) {
      console.warn('No state ID provided to getMockCities');
      return { data: [] };
    }
    
    const citiesByState = {
      // Malaysia (Country ID: 1)
      101: [ // Selangor
        { id: 1001, name: 'Shah Alam', stateId: 101 },
        { id: 1002, name: 'Petaling Jaya', stateId: 101 },
        { id: 1003, name: 'Subang Jaya', stateId: 101 },
        { id: 1004, name: 'Klang', stateId: 101 },
        { id: 1005, name: 'Kajang', stateId: 101 }
      ],
      102: [ // Kuala Lumpur
        { id: 1101, name: 'Bukit Bintang', stateId: 102 },
        { id: 1102, name: 'Cheras', stateId: 102 },
        { id: 1103, name: 'Wangsa Maju', stateId: 102 },
        { id: 1104, name: 'Brickfields', stateId: 102 },
        { id: 1105, name: 'Kepong', stateId: 102 }
      ],
      103: [ // Penang
        { id: 1201, name: 'George Town', stateId: 103 },
        { id: 1202, name: 'Butterworth', stateId: 103 },
        { id: 1203, name: 'Bayan Lepas', stateId: 103 },
        { id: 1204, name: 'Bukit Mertajam', stateId: 103 },
        { id: 1205, name: 'Nibong Tebal', stateId: 103 }
      ],
      104: [ // Johor
        { id: 1301, name: 'Johor Bahru', stateId: 104 },
        { id: 1302, name: 'Iskandar Puteri', stateId: 104 },
        { id: 1303, name: 'Batu Pahat', stateId: 104 },
        { id: 1304, name: 'Muar', stateId: 104 },
        { id: 1305, name: 'Kluang', stateId: 104 }
      ],
      105: [ // Sabah
        { id: 1401, name: 'Kota Kinabalu', stateId: 105 },
        { id: 1402, name: 'Sandakan', stateId: 105 },
        { id: 1403, name: 'Tawau', stateId: 105 },
        { id: 1404, name: 'Lahad Datu', stateId: 105 },
        { id: 1405, name: 'Keningau', stateId: 105 }
      ],
      106: [ // Sarawak
        { id: 1501, name: 'Kuching', stateId: 106 },
        { id: 1502, name: 'Miri', stateId: 106 },
        { id: 1503, name: 'Sibu', stateId: 106 },
        { id: 1504, name: 'Bintulu', stateId: 106 },
        { id: 1505, name: 'Limbang', stateId: 106 }
      ],
      // Singapore (Country ID: 2)
      201: [ // Central Region
        { id: 2001, name: 'Downtown Core', stateId: 201 },
        { id: 2002, name: 'Orchard', stateId: 201 },
        { id: 2003, name: 'Marina South', stateId: 201 },
        { id: 2004, name: 'Marina East', stateId: 201 },
        { id: 2005, name: 'Rochor', stateId: 201 }
      ],
      202: [ // East Region
        { id: 2101, name: 'Changi', stateId: 202 },
        { id: 2102, name: 'Bedok', stateId: 202 },
        { id: 2103, name: 'Tampines', stateId: 202 },
        { id: 2104, name: 'Pasir Ris', stateId: 202 },
        { id: 2105, name: 'East Coast', stateId: 202 }
      ],
      301: [ // California
        { id: 3001, name: 'Los Angeles', stateId: 301 },
        { id: 3002, name: 'San Francisco', stateId: 301 },
        { id: 3003, name: 'San Diego', stateId: 301 },
        { id: 3004, name: 'San Jose', stateId: 301 },
        { id: 3005, name: 'Sacramento', stateId: 301 }
      ],
      302: [ // New York
        { id: 3101, name: 'New York City', stateId: 302 },
        { id: 3102, name: 'Buffalo', stateId: 302 },
        { id: 3103, name: 'Rochester', stateId: 302 },
        { id: 3104, name: 'Syracuse', stateId: 302 },
        { id: 3105, name: 'Albany', stateId: 302 }
      ],
      401: [ // England
        { id: 4001, name: 'London', stateId: 401 },
        { id: 4002, name: 'Manchester', stateId: 401 },
        { id: 4003, name: 'Birmingham', stateId: 401 },
        { id: 4004, name: 'Liverpool', stateId: 401 },
        { id: 4005, name: 'Leeds', stateId: 401 }
      ],
      402: [ // Scotland
        { id: 4101, name: 'Edinburgh', stateId: 402 },
        { id: 4102, name: 'Glasgow', stateId: 402 },
        { id: 4103, name: 'Aberdeen', stateId: 402 },
        { id: 4104, name: 'Dundee', stateId: 402 },
        { id: 4105, name: 'Inverness', stateId: 402 }
      ],
      501: [ // New South Wales
        { id: 5001, name: 'Sydney', stateId: 501 },
        { id: 5002, name: 'Newcastle', stateId: 501 },
        { id: 5003, name: 'Wollongong', stateId: 501 },
        { id: 5004, name: 'Coffs Harbour', stateId: 501 },
        { id: 5005, name: 'Wagga Wagga', stateId: 501 }
      ],
      502: [ // Victoria
        { id: 5101, name: 'Melbourne', stateId: 502 },
        { id: 5102, name: 'Geelong', stateId: 502 },
        { id: 5103, name: 'Ballarat', stateId: 502 },
        { id: 5104, name: 'Bendigo', stateId: 502 },
        { id: 5105, name: 'Shepparton', stateId: 502 }
      ],
      601: [ // Ontario
        { id: 6001, name: 'Toronto', stateId: 601 },
        { id: 6002, name: 'Ottawa', stateId: 601 },
        { id: 6003, name: 'Mississauga', stateId: 601 },
        { id: 6004, name: 'Hamilton', stateId: 601 },
        { id: 6005, name: 'London', stateId: 601 }
      ],
      602: [ // Quebec
        { id: 6101, name: 'Montreal', stateId: 602 },
        { id: 6102, name: 'Quebec City', stateId: 602 },
        { id: 6103, name: 'Laval', stateId: 602 },
        { id: 6104, name: 'Gatineau', stateId: 602 },
        { id: 6105, name: 'Sherbrooke', stateId: 602 }
      ],
      603: [ // British Columbia
        { id: 6201, name: 'Vancouver', stateId: 603 },
        { id: 6202, name: 'Victoria', stateId: 603 },
        { id: 6203, name: 'Kelowna', stateId: 603 },
        { id: 6204, name: 'Abbotsford', stateId: 603 },
        { id: 6205, name: 'Nanaimo', stateId: 603 }
      ],
      604: [ // Alberta
        { id: 6301, name: 'Calgary', stateId: 604 },
        { id: 6302, name: 'Edmonton', stateId: 604 },
        { id: 6303, name: 'Red Deer', stateId: 604 },
        { id: 6304, name: 'Lethbridge', stateId: 604 },
        { id: 6305, name: 'Fort McMurray', stateId: 604 }
      ],
      605: [ // Manitoba
        { id: 6401, name: 'Winnipeg', stateId: 605 },
        { id: 6402, name: 'Brandon', stateId: 605 },
        { id: 6403, name: 'Steinbach', stateId: 605 },
        { id: 6404, name: 'Thompson', stateId: 605 },
        { id: 6405, name: 'Portage la Prairie', stateId: 605 }
      ],
      606: [ // Saskatchewan
        { id: 6501, name: 'Saskatoon', stateId: 606 },
        { id: 6502, name: 'Regina', stateId: 606 },
        { id: 6503, name: 'Prince Albert', stateId: 606 },
        { id: 6504, name: 'Moose Jaw', stateId: 606 },
        { id: 6505, name: 'Swift Current', stateId: 606 }
      ],
      607: [ // Nova Scotia
        { id: 6601, name: 'Halifax', stateId: 607 },
        { id: 6602, name: 'Sydney', stateId: 607 },
        { id: 6603, name: 'Dartmouth', stateId: 607 },
        { id: 6604, name: 'Truro', stateId: 607 },
        { id: 6605, name: 'New Glasgow', stateId: 607 }
      ],
      608: [ // New Brunswick
        { id: 6701, name: 'Moncton', stateId: 608 },
        { id: 6702, name: 'Saint John', stateId: 608 },
        { id: 6703, name: 'Fredericton', stateId: 608 },
        { id: 6704, name: 'Bathurst', stateId: 608 },
        { id: 6705, name: 'Edmundston', stateId: 608 }
      ],
      609: [ // Newfoundland and Labrador
        { id: 6801, name: 'St. John\'s', stateId: 609 },
        { id: 6802, name: 'Mount Pearl', stateId: 609 },
        { id: 6803, name: 'Corner Brook', stateId: 609 },
        { id: 6804, name: 'Grand Falls-Windsor', stateId: 609 },
        { id: 6805, name: 'Gander', stateId: 609 }
      ],
      610: [ // Prince Edward Island
        { id: 6901, name: 'Charlottetown', stateId: 610 },
        { id: 6902, name: 'Summerside', stateId: 610 },
        { id: 6903, name: 'Stratford', stateId: 610 },
        { id: 6904, name: 'Cornwall', stateId: 610 },
        { id: 6905, name: 'Montague', stateId: 610 }
      ],
      701: [ // Beijing
        { id: 7001, name: 'Dongcheng', stateId: 701 },
        { id: 7002, name: 'Xicheng', stateId: 701 },
        { id: 7003, name: 'Chaoyang', stateId: 701 },
        { id: 7004, name: 'Haidian', stateId: 701 },
        { id: 7005, name: 'Fengtai', stateId: 701 }
      ],
  //   702: [ // Shanghai
  //     { id: 7101, name: 'Pudong', stateId: 702 },
  //     { id: 7102, name: 'Huangpu', stateId: 702 },
  //     { id: 7103, name: 'Jing\'an', stateId: 702 },
  //     { id: 7104, name: 'Xuhui', stateId: 702 },
  //     { id: 7105, name: 'Changning', stateId: 702 }
  //     ],
  //     801: [ // Maharashtra
  //       { id: 8001, name: 'Mumbai', stateId: 801 },
  //       { id: 8002, name: 'Pune', stateId: 801 },
  //       { id: 8003, name: 'Nagpur', stateId: 801 },
  //       { id: 8004, name: 'Thane', stateId: 801 },
  //       { id: 8005, name: 'Nashik', stateId: 801 }
  //     ],
      901: [ // Tokyo
        { id: 9001, name: 'Shinjuku', stateId: 901 },
        { id: 9002, name: 'Shibuya', stateId: 901 },
        { id: 9003, name: 'Minato', stateId: 901 },
        { id: 9004, name: 'Chiyoda', stateId: 901 },
        { id: 9005, name: 'Chuo', stateId: 901 }
      ],
      1001: [ // Bavaria
        { id: 10001, name: 'Munich', stateId: 1001 },
        { id: 10002, name: 'Nuremberg', stateId: 1001 },
        { id: 10003, name: 'Augsburg', stateId: 1001 },
        { id: 10004, name: 'Regensburg', stateId: 1001 },
        { id: 10005, name: 'Ingolstadt', stateId: 1001 }
      ],
      1101: [ // Île-de-France
        { id: 11001, name: 'Paris', stateId: 1101 },
        { id: 11002, name: 'Versailles', stateId: 1101 },
        { id: 11003, name: 'Saint-Denis', stateId: 1101 },
        { id: 11004, name: 'Boulogne-Billancourt', stateId: 1101 },
        { id: 11005, name: 'Argenteuil', stateId: 1101 }
      ],
      1201: [ // Lombardy
        { id: 12001, name: 'Milan', stateId: 1201 },
        { id: 12002, name: 'Brescia', stateId: 1201 },
        { id: 12003, name: 'Monza', stateId: 1201 },
        { id: 12004, name: 'Bergamo', stateId: 1201 },
        { id: 12005, name: 'Como', stateId: 1201 }
      ],
      1301: [ // São Paulo
        { id: 13001, name: 'São Paulo City', stateId: 1301 },
        { id: 13002, name: 'Campinas', stateId: 1301 },
        { id: 13003, name: 'Santos', stateId: 1301 },
        { id: 13004, name: 'São José dos Campos', stateId: 1301 },
        { id: 13005, name: 'Ribeirão Preto', stateId: 1301 }
      ],
      1401: [ // Seoul
        { id: 14001, name: 'Gangnam', stateId: 1401 },
        { id: 14002, name: 'Mapo', stateId: 1401 },
        { id: 14003, name: 'Jongno', stateId: 1401 },
        { id: 14004, name: 'Yongsan', stateId: 1401 },
        { id: 14005, name: 'Songpa', stateId: 1401 }
      ],
      1501: [ // Jakarta
        { id: 15001, name: 'Central Jakarta', stateId: 1501 },
        { id: 15002, name: 'South Jakarta', stateId: 1501 },
        { id: 15003, name: 'North Jakarta', stateId: 1501 },
        { id: 15004, name: 'East Jakarta', stateId: 1501 },
        { id: 15005, name: 'West Jakarta', stateId: 1501 }
      ],
      1601: [ // Bangkok
        { id: 16001, name: 'Pathum Wan', stateId: 1601 },
        { id: 16002, name: 'Watthana', stateId: 1601 },
        { id: 16003, name: 'Khlong Toei', stateId: 1601 },
        { id: 16004, name: 'Bang Rak', stateId: 1601 },
        { id: 16005, name: 'Sathon', stateId: 1601 }
      ],
      1701: [ // Hanoi
        { id: 17001, name: 'Hoan Kiem', stateId: 1701 },
        { id: 17002, name: 'Ba Dinh', stateId: 1701 },
        { id: 17003, name: 'Dong Da', stateId: 1701 },
        { id: 17004, name: 'Hai Ba Trung', stateId: 1701 },
        { id: 17005, name: 'Tay Ho', stateId: 1701 }
      ],
      1801: [ // Metro Manila
        { id: 18001, name: 'Quezon City', stateId: 1801 },
        { id: 18002, name: 'Manila', stateId: 1801 },
        { id: 18003, name: 'Makati', stateId: 1801 },
        { id: 18004, name: 'Taguig', stateId: 1801 },
        { id: 18005, name: 'Pasig', stateId: 1801 }
      ],
      1901: [ // Madrid
        { id: 19001, name: 'Madrid City', stateId: 1901 },
        { id: 19002, name: 'Móstoles', stateId: 1901 },
        { id: 19003, name: 'Alcalá de Henares', stateId: 1901 },
        { id: 19004, name: 'Fuenlabrada', stateId: 1901 },
        { id: 19005, name: 'Leganés', stateId: 1901 }
      ],
      2001: [ // North Holland
        { id: 20001, name: 'Amsterdam', stateId: 2001 },
        { id: 20002, name: 'Haarlem', stateId: 2001 },
        { id: 20003, name: 'Zaanstad', stateId: 2001 },
        { id: 20004, name: 'Haarlemmermeer', stateId: 2001 },
        { id: 20005, name: 'Alkmaar', stateId: 2001 }
      ],
      2101: [ // Mexico City
        { id: 21011, name: 'Cuauhtémoc', stateId: 2101 },
        { id: 21012, name: 'Miguel Hidalgo', stateId: 2101 },
        { id: 21013, name: 'Benito Juárez', stateId: 2101 },
        { id: 21014, name: 'Coyoacán', stateId: 2101 },
        { id: 21015, name: 'Tlalpan', stateId: 2101 }
      ],
      2201: [ // Istanbul
        { id: 22001, name: 'Fatih', stateId: 2201 },
        { id: 22002, name: 'Beyoğlu', stateId: 2201 },
        { id: 22003, name: 'Kadıköy', stateId: 2201 },
        { id: 22004, name: 'Üsküdar', stateId: 2201 },
        { id: 22005, name: 'Şişli', stateId: 2201 }
      ],
      2301: [ // Zurich
        { id: 23001, name: 'Zurich City', stateId: 2301 },
        { id: 23002, name: 'Winterthur', stateId: 2301 },
        { id: 23003, name: 'Uster', stateId: 2301 },
        { id: 23004, name: 'Dübendorf', stateId: 2301 },
        { id: 23005, name: 'Dietikon', stateId: 2301 }
      ],
      2401: [ // Stockholm
        { id: 24001, name: 'Stockholm City', stateId: 2401 },
        { id: 24002, name: 'Solna', stateId: 2401 },
        { id: 24003, name: 'Sundbyberg', stateId: 2401 },
        { id: 24004, name: 'Nacka', stateId: 2401 },
        { id: 24005, name: 'Lidingö', stateId: 2401 }
      ],
      2501: [ // Auckland
        { id: 25001, name: 'Auckland Central', stateId: 2501 },
        { id: 25002, name: 'North Shore', stateId: 2501 },
        { id: 25003, name: 'Manukau', stateId: 2501 },
        { id: 25004, name: 'Waitakere', stateId: 2501 },
        { id: 25005, name: 'Papakura', stateId: 2501 }
      ],
      2601: [ // Dublin
        { id: 26001, name: 'Dublin City', stateId: 2601 },
        { id: 26002, name: 'Dún Laoghaire', stateId: 2601 },
        { id: 26003, name: 'Tallaght', stateId: 2601 },
        { id: 26004, name: 'Swords', stateId: 2601 },
        { id: 26005, name: 'Bray', stateId: 2601 }
      ],
      2701: [ // Brussels
        { id: 27001, name: 'Brussels City', stateId: 2701 },
        { id: 27002, name: 'Schaerbeek', stateId: 2701 },
        { id: 27003, name: 'Anderlecht', stateId: 2701 },
        { id: 27004, name: 'Molenbeek', stateId: 2701 },
        { id: 27005, name: 'Ixelles', stateId: 2701 }
      ],
      2801: [ // Oslo
        { id: 28001, name: 'Oslo Central', stateId: 2801 },
        { id: 28002, name: 'Frogner', stateId: 2801 },
        { id: 28003, name: 'Grünerløkka', stateId: 2801 },
        { id: 28004, name: 'St. Hanshaugen', stateId: 2801 },
        { id: 28005, name: 'Sagene', stateId: 2801 }
      ],
      2901: [ // Capital Region
        { id: 29001, name: 'Copenhagen', stateId: 2901 },
        { id: 29002, name: 'Frederiksberg', stateId: 2901 },
        { id: 29003, name: 'Helsingør', stateId: 2901 },
        { id: 29004, name: 'Roskilde', stateId: 2901 },
        { id: 29005, name: 'Hillerød', stateId: 2901 }
      ],
      3001: [ // Uusimaa
        { id: 30001, name: 'Helsinki', stateId: 3001 },
        { id: 30002, name: 'Espoo', stateId: 3001 },
        { id: 30003, name: 'Vantaa', stateId: 3001 },
        { id: 30004, name: 'Porvoo', stateId: 3001 },
        { id: 30005, name: 'Lohja', stateId: 3001 }
      ],




      108: [ // Kedah
        { id: 1801, name: 'Alor Setar', stateId: 108 },
        { id: 1802, name: 'Sungai Petani', stateId: 108 },
        { id: 1803, name: 'Kulim', stateId: 108 },
        { id: 1804, name: 'Langkawi', stateId: 108 },
        { id: 1805, name: 'Jitra', stateId: 108 },
        { id: 1806, name: 'Yan', stateId: 108 },
        { id: 1807, name: 'Kuah', stateId: 108 },
        { id: 1808, name: 'Gurun', stateId: 108 },
        { id: 1809, name: 'Baling', stateId: 108 }
      ],
      // 202: [ // Singapore - East Region (duplicate removed - already defined above)
      //   { id: 2101, name: 'Changi', stateId: 202 },
      //   { id: 2102, name: 'Bedok', stateId: 202 },
      //   { id: 2103, name: 'Tampines', stateId: 202 },
      //   { id: 2104, name: 'Pasir Ris', stateId: 202 },
      //   { id: 2105, name: 'East Coast', stateId: 202 }
      // ],
      // 402: [ // Scotland (duplicate removed - already defined above)
      //   { id: 4101, name: 'Edinburgh', stateId: 402 },
      //   { id: 4102, name: 'Glasgow', stateId: 402 },
      //   { id: 4103, name: 'Aberdeen', stateId: 402 },
      //   { id: 4104, name: 'Dundee', stateId: 402 },
      //   { id: 4105, name: 'Inverness', stateId: 402 }
      // ],
      // 502: [ // Victoria (duplicate removed - already defined above)
      //   { id: 5101, name: 'Melbourne', stateId: 502 },
      //   { id: 5102, name: 'Geelong', stateId: 502 },
      //   { id: 5103, name: 'Ballarat', stateId: 502 },
      //   { id: 5104, name: 'Bendigo', stateId: 502 },
      //   { id: 5105, name: 'Shepparton', stateId: 502 }
      // ],
      // 505: [ // South Australia (duplicate removed - already defined above)
      //   { id: 5401, name: 'Adelaide', stateId: 505 },
      //   { id: 5402, name: 'Mount Gambier', stateId: 505 },
      //   { id: 5403, name: 'Whyalla', stateId: 505 },
      //   { id: 5404, name: 'Murray Bridge', stateId: 505 },
      //   { id: 5405, name: 'Port Lincoln', stateId: 505 }
      // ],
      // 506: [ // Tasmania (duplicate removed - already defined above)
      //   { id: 5501, name: 'Hobart', stateId: 506 },
      //   { id: 5502, name: 'Launceston', stateId: 506 },
      //   { id: 5503, name: 'Devonport', stateId: 506 },
      //   { id: 5504, name: 'Burnie', stateId: 506 },
      //   { id: 5505, name: 'Kingston', stateId: 506 }
      // ],
      // 507: [ // Australian Capital Territory (duplicate removed - already defined above)
      //   { id: 5601, name: 'Canberra', stateId: 507 },
      //   { id: 5602, name: 'Belconnen', stateId: 507 },
      //   { id: 5603, name: 'Tuggeranong', stateId: 507 },
      //   { id: 5604, name: 'Woden Valley', stateId: 507 },
      //   { id: 5605, name: 'Gungahlin', stateId: 507 }
      // ],
      // 508: [ // Northern Territory (duplicate removed - already defined above)
      //   { id: 5701, name: 'Darwin', stateId: 508 },
      //   { id: 5702, name: 'Alice Springs', stateId: 508 },
      //   { id: 5703, name: 'Katherine', stateId: 508 },
      //   { id: 5704, name: 'Tennant Creek', stateId: 508 },
      //   { id: 5705, name: 'Nhulunbuy', stateId: 508 }
      // ],
      // 603: [ // British Columbia (duplicate removed - already defined above)
      //   { id: 6201, name: 'Vancouver', stateId: 603 },
      //   { id: 6202, name: 'Victoria', stateId: 603 },
      //   { id: 6203, name: 'Kelowna', stateId: 603 },
      //   { id: 6204, name: 'Abbotsford', stateId: 603 },
      //   { id: 6205, name: 'Nanaimo', stateId: 603 }
      // ],
      // 604: [ // Alberta (duplicate removed - already defined above)
      //   { id: 6301, name: 'Calgary', stateId: 604 },
      //   { id: 6302, name: 'Edmonton', stateId: 604 },
      //   { id: 6303, name: 'Red Deer', stateId: 604 },
      //   { id: 6304, name: 'Lethbridge', stateId: 604 },
      //   { id: 6305, name: 'Fort McMurray', stateId: 604 }
      // ],
      // 605: [ // Manitoba (duplicate removed - already defined above)
      //   { id: 6401, name: 'Winnipeg', stateId: 605 },
      //   { id: 6402, name: 'Brandon', stateId: 605 },
      //   { id: 6403, name: 'Steinbach', stateId: 605 },
      //   { id: 6404, name: 'Thompson', stateId: 605 },
      //   { id: 6405, name: 'Portage la Prairie', stateId: 605 }
      // ],
      // 606: [ // Saskatchewan (duplicate removed - already defined above)
      //   { id: 6501, name: 'Saskatoon', stateId: 606 },
      //   { id: 6502, name: 'Regina', stateId: 606 },
      //   { id: 6503, name: 'Prince Albert', stateId: 606 },
      //   { id: 6504, name: 'Moose Jaw', stateId: 606 },
      //   { id: 6505, name: 'Swift Current', stateId: 606 }
      // ],
      // 701: [ // Beijing (duplicate removed - already defined above)
      //   { id: 7001, name: 'Dongcheng', stateId: 701 },
      //   { id: 7002, name: 'Xicheng', stateId: 701 },
      //   { id: 7003, name: 'Chaoyang', stateId: 701 },
      //   { id: 7004, name: 'Haidian', stateId: 701 },
      //   { id: 7005, name: 'Fengtai', stateId: 701 }
      // ],
  //   702: [ // Shanghai
  //     { id: 7101, name: 'Pudong', stateId: 702 },
  //     { id: 7102, name: 'Huangpu', stateId: 702 },
  //     { id: 7103, name: 'Jing\'an', stateId: 702 },
  //     { id: 7104, name: 'Xuhui', stateId: 702 },
  //     { id: 7105, name: 'Changning', stateId: 702 }
  //     ],
  //     801: [ // Maharashtra
  //       { id: 8001, name: 'Mumbai', stateId: 801 },
  //       { id: 8002, name: 'Pune', stateId: 801 },
  //       { id: 8003, name: 'Nagpur', stateId: 801 },
  //       { id: 8004, name: 'Thane', stateId: 801 },
  //       { id: 8005, name: 'Nashik', stateId: 801 }
  //     ],
  //     901: [ // Tokyo
  //       { id: 9001, name: 'Shinjuku', stateId: 901 },
  //       { id: 9002, name: 'Shibuya', stateId: 901 },
  //       { id: 9003, name: 'Minato', stateId: 901 },
  //       { id: 9004, name: 'Chiyoda', stateId: 901 },
  //       { id: 9005, name: 'Chuo', stateId: 901 }
  //     ],
  //     1001: [ // Bavaria
  //       { id: 10001, name: 'Munich', stateId: 1001 },
  //       { id: 10002, name: 'Nuremberg', stateId: 1001 },
  //       { id: 10003, name: 'Augsburg', stateId: 1001 },
  //       { id: 10004, name: 'Regensburg', stateId: 1001 },
  //       { id: 10005, name: 'Ingolstadt', stateId: 1001 }
  //     ]
    };

  //   return { data: citiesByState[stateId] || [] };
  // }

  // REMOVING DUPLICATE METHOD DEFINITION - COMMENTED OUT ENTIRE BLOCK
  //       { id: 1101, name: 'Bukit Bintang', stateId: 102 },
  //       { id: 1102, name: 'Cheras', stateId: 102 },
  //       { id: 1103, name: 'Wangsa Maju', stateId: 102 },
  //       { id: 1104, name: 'Kepong', stateId: 102 },
  //       { id: 1105, name: 'Batu', stateId: 102 }
  //     ],
  //     // Singapore
  //     201: [ // Central Region
  //       { id: 2001, name: 'Downtown Core', stateId: 201 },
  //       { id: 2002, name: 'Marina East', stateId: 201 },
  //       { id: 2003, name: 'Marina South', stateId: 201 },
  //       { id: 2004, name: 'Orchard', stateId: 201 },
  //       { id: 2005, name: 'Rochor', stateId: 201 }
  //     ],
  //     // United States
  //     301: [ // California
  //       { id: 3001, name: 'Los Angeles', stateId: 301 },
  //       { id: 3002, name: 'San Francisco', stateId: 301 },
  //       { id: 3003, name: 'San Diego', stateId: 301 },
  //       { id: 3004, name: 'San Jose', stateId: 301 },
  //       { id: 3005, name: 'Sacramento', stateId: 301 }
  //     ],
  //     302: [ // New York
  //       { id: 3101, name: 'New York City', stateId: 302 },
  //       { id: 3102, name: 'Buffalo', stateId: 302 },
  //       { id: 3103, name: 'Rochester', stateId: 302 },
  //       { id: 3104, name: 'Syracuse', stateId: 302 },
  //       { id: 3105, name: 'Albany', stateId: 302 }
  //     ],
  //     // United Kingdom
  //     401: [ // England
  //       { id: 4001, name: 'London', stateId: 401 },
  //       { id: 4002, name: 'Manchester', stateId: 401 },
  //       { id: 4003, name: 'Birmingham', stateId: 401 },
  //       { id: 4004, name: 'Liverpool', stateId: 401 },
  //       { id: 4005, name: 'Leeds', stateId: 401 }
  //     ],
  //     // Australia
  //     501: [ // New South Wales
  //       { id: 5001, name: 'Sydney', stateId: 501 },
  //       { id: 5002, name: 'Newcastle', stateId: 501 },
  //       { id: 5003, name: 'Wollongong', stateId: 501 },
  //       { id: 5004, name: 'Coffs Harbour', stateId: 501 },
  //       { id: 5005, name: 'Wagga Wagga', stateId: 501 }
  //     ],
  //     // Canada
  //     601: [ // Ontario
  //       { id: 6001, name: 'Toronto', stateId: 601 },
  //       { id: 6002, name: 'Ottawa', stateId: 601 },
  //       { id: 6003, name: 'Mississauga', stateId: 601 },
  //       { id: 6004, name: 'Hamilton', stateId: 601 },
  //       { id: 6005, name: 'London', stateId: 601 }
  //     ],
  //     602: [ // Quebec
  //       { id: 6101, name: 'Montreal', stateId: 602 },
  //       { id: 6102, name: 'Quebec City', stateId: 602 },
  //       { id: 6103, name: 'Laval', stateId: 602 },
  //       { id: 6104, name: 'Gatineau', stateId: 602 },
  //       { id: 6105, name: 'Sherbrooke', stateId: 602 }
  //     ],
  //     // China
  //     701: [ // Beijing
  //       { id: 7001, name: 'Dongcheng', stateId: 701 },
  //       { id: 7002, name: 'Xicheng', stateId: 701 },
  //       { id: 7003, name: 'Chaoyang', stateId: 701 },
  //       { id: 7004, name: 'Haidian', stateId: 701 },
  //       { id: 7005, name: 'Fengtai', stateId: 701 }
  //     ],
  //     702: [ // Shanghai
  //       { id: 7101, name: 'Pudong', stateId: 702 },
  //       { id: 7102, name: 'Huangpu', stateId: 702 },
  //       { id: 7103, name: 'Jing\'an', stateId: 702 },
  //       { id: 7104, name: 'Xuhui', stateId: 702 },
  //       { id: 7105, name: 'Changning', stateId: 702 }
  //     ],
  //     // India
  //     801: [ // Maharashtra
  //       { id: 8001, name: 'Mumbai', stateId: 801 },
  //       { id: 8002, name: 'Pune', stateId: 801 },
  //       { id: 8003, name: 'Nagpur', stateId: 801 },
  //       { id: 8004, name: 'Thane', stateId: 801 },
  //       { id: 8005, name: 'Nashik', stateId: 801 }
  //     ],
  //     // Japan
  //     901: [ // Tokyo
  //       { id: 9001, name: 'Shinjuku', stateId: 901 },
  //       { id: 9002, name: 'Shibuya', stateId: 901 },
  //       { id: 9003, name: 'Minato', stateId: 901 },
  //       { id: 9004, name: 'Chiyoda', stateId: 901 },
  //       { id: 9005, name: 'Chuo', stateId: 901 }
  //     ],
  //     // Germany
  //     1001: [ // Bavaria
  //       { id: 10001, name: 'Munich', stateId: 1001 },
  //       { id: 10002, name: 'Nuremberg', stateId: 1001 },
  //       { id: 10003, name: 'Augsburg', stateId: 1001 },
  //       { id: 10004, name: 'Regensburg', stateId: 1001 },
  //       { id: 10005, name: 'Ingolstadt', stateId: 1001 }
  //     ],
  //     // Henan
  //     707: [ // Henan
  //       { id: 7601, name: 'Zhengzhou', stateId: 707 },
  //       { id: 7602, name: 'Luoyang', stateId: 707 },
  //       { id: 7603, name: 'Nanyang', stateId: 707 },
  //       { id: 7604, name: 'Xinxiang', stateId: 707 },
  //       { id: 7605, name: 'Anyang', stateId: 707 }
  //     ],
  //     708: [ // Shandong
  //       { id: 7701, name: 'Jinan', stateId: 708 },
  //       { id: 7702, name: 'Qingdao', stateId: 708 },
  //       { id: 7703, name: 'Yantai', stateId: 708 },
  //       { id: 7704, name: 'Weifang', stateId: 708 },
  //       { id: 7705, name: 'Weihai', stateId: 708 }
  //     ],
  //     803: [ // Karnataka
  //       { id: 8201, name: 'Bangalore', stateId: 803 },
  //       { id: 8202, name: 'Mysore', stateId: 803 },
  //       { id: 8203, name: 'Hubli-Dharwad', stateId: 803 },
  //       { id: 8204, name: 'Mangalore', stateId: 803 },
  //       { id: 8205, name: 'Belgaum', stateId: 803 }
  //     ],
  //     804: [ // Delhi
  //       { id: 8301, name: 'New Delhi', stateId: 804 },
  //       { id: 8302, name: 'North Delhi', stateId: 804 },
  //       { id: 8303, name: 'South Delhi', stateId: 804 },
  //       { id: 8304, name: 'East Delhi', stateId: 804 },
  //       { id: 8305, name: 'West Delhi', stateId: 804 }
  //     ],
  //     805: [ // Gujarat
  //       { id: 8401, name: 'Ahmedabad', stateId: 805 },
  //       { id: 8402, name: 'Surat', stateId: 805 },
  //       { id: 8403, name: 'Vadodara', stateId: 805 },
  //       { id: 8404, name: 'Rajkot', stateId: 805 },
  //       { id: 8405, name: 'Bhavnagar', stateId: 805 }
  //     ],
  //     806: [ // Uttar Pradesh
  //       { id: 8501, name: 'Lucknow', stateId: 806 },
  //       { id: 8502, name: 'Kanpur', stateId: 806 },
  //       { id: 8503, name: 'Agra', stateId: 806 },
  //       { id: 8504, name: 'Varanasi', stateId: 806 },
  //       { id: 8505, name: 'Meerut', stateId: 806 }
  //     ],
  //     807: [ // West Bengal
  //       { id: 8601, name: 'Kolkata', stateId: 807 },
  //       { id: 8602, name: 'Howrah', stateId: 807 },
  //       { id: 8603, name: 'Durgapur', stateId: 807 },
  //       { id: 8604, name: 'Asansol', stateId: 807 },
  //       { id: 8605, name: 'Siliguri', stateId: 807 }
  //     ],
  //     808: [ // Telangana
  //       { id: 8701, name: 'Hyderabad', stateId: 808 },
  //       { id: 8702, name: 'Warangal', stateId: 808 },
  //       { id: 8703, name: 'Nizamabad', stateId: 808 },
  //       { id: 8704, name: 'Karimnagar', stateId: 808 },
  //       { id: 8705, name: 'Khammam', stateId: 808 }
  //     ],
  //     903: [ // Kanagawa
  //       { id: 9201, name: 'Yokohama', stateId: 903 },
  //       { id: 9202, name: 'Kawasaki', stateId: 903 },
  //       { id: 9203, name: 'Sagamihara', stateId: 903 },
  //       { id: 9204, name: 'Fujisawa', stateId: 903 },
  //       { id: 9205, name: 'Kamakura', stateId: 903 }
  //     ],
  //     904: [ // Aichi
  //       { id: 9301, name: 'Nagoya', stateId: 904 },
  //       { id: 9302, name: 'Toyota', stateId: 904 },
  //       { id: 9303, name: 'Toyohashi', stateId: 904 },
  //       { id: 9304, name: 'Okazaki', stateId: 904 },
  //       { id: 9305, name: 'Ichinomiya', stateId: 904 }
  //     ],
  //     905: [ // Hokkaido
  //       { id: 9401, name: 'Sapporo', stateId: 905 },
  //       { id: 9402, name: 'Asahikawa', stateId: 905 },
  //       { id: 9403, name: 'Hakodate', stateId: 905 },
  //       { id: 9404, name: 'Obihiro', stateId: 905 },
  //       { id: 9405, name: 'Kushiro', stateId: 905 }
  //     ],
  //     906: [ // Fukuoka
  //       { id: 9501, name: 'Fukuoka City', stateId: 906 },
  //       { id: 9502, name: 'Kitakyushu', stateId: 906 },
  //       { id: 9503, name: 'Kurume', stateId: 906 },
  //       { id: 9504, name: 'Omuta', stateId: 906 },
  //       { id: 9505, name: 'Iizuka', stateId: 906 }
  //     ],
  //     1003: [ // Baden-Württemberg
  //       { id: 10201, name: 'Stuttgart', stateId: 1003 },
  //       { id: 10202, name: 'Karlsruhe', stateId: 1003 },
  //       { id: 10203, name: 'Mannheim', stateId: 1003 },
  //       { id: 10204, name: 'Freiburg', stateId: 1003 },
  //       { id: 10205, name: 'Heidelberg', stateId: 1003 }
  //     ],
  //     1004: [ // Lower Saxony
  //       { id: 10301, name: 'Hanover', stateId: 1004 },
  //       { id: 10302, name: 'Braunschweig', stateId: 1004 },
  //       { id: 10303, name: 'Osnabrück', stateId: 1004 },
  //       { id: 10304, name: 'Oldenburg', stateId: 1004 },
  //       { id: 10305, name: 'Wolfsburg', stateId: 1004 }
  //     ],
  //     1005: [ // Hesse
  //       { id: 10401, name: 'Frankfurt', stateId: 1005 },
  //       { id: 10402, name: 'Wiesbaden', stateId: 1005 },
  //       { id: 10403, name: 'Kassel', stateId: 1005 },
  //       { id: 10404, name: 'Darmstadt', stateId: 1005 },
  //       { id: 10405, name: 'Offenbach', stateId: 1005 }
  //     ],
  //     1006: [ // Berlin
  //       { id: 10501, name: 'Mitte', stateId: 1006 },
  //       { id: 10502, name: 'Friedrichshain-Kreuzberg', stateId: 1006 },
  //       { id: 10503, name: 'Pankow', stateId: 1006 },
  //       { id: 10504, name: 'Charlottenburg-Wilmersdorf', stateId: 1006 },
  //       { id: 10505, name: 'Spandau', stateId: 1006 }
  //     ],
  //     // United Kingdom
  //     401: [ // England
  //       { id: 4001, name: 'London', stateId: 401 },
  //       { id: 4002, name: 'Manchester', stateId: 401 },
  //       { id: 4003, name: 'Birmingham', stateId: 401 },
  //       { id: 4004, name: 'Liverpool', stateId: 401 },
  //       { id: 4005, name: 'Leeds', stateId: 401 },
  //       { id: 4006, name: 'Bristol', stateId: 401 },
  //       { id: 4007, name: 'Newcastle', stateId: 401 },
  //       { id: 4008, name: 'Sheffield', stateId: 401 }
  //     ],
  //     402: [ // Scotland
  //       { id: 4101, name: 'Edinburgh', stateId: 402 },
  //       { id: 4102, name: 'Glasgow', stateId: 402 },
  //       { id: 4103, name: 'Aberdeen', stateId: 402 },
  //       { id: 4104, name: 'Dundee', stateId: 402 },
  //       { id: 4105, name: 'Inverness', stateId: 402 },
  //       { id: 4106, name: 'Stirling', stateId: 402 }
  //     ],
  //     // Australia
  //     501: [ // New South Wales
  //       { id: 5001, name: 'Sydney', stateId: 501 },
  //       { id: 5002, name: 'Newcastle', stateId: 501 },
  //       { id: 5003, name: 'Wollongong', stateId: 501 },
  //       { id: 5004, name: 'Coffs Harbour', stateId: 501 },
  //       { id: 5005, name: 'Wagga Wagga', stateId: 501 },
  //       { id: 5006, name: 'Albury', stateId: 501 }
  //     ],
  //     502: [ // Victoria
  //       { id: 5101, name: 'Melbourne', stateId: 502 },
  //       { id: 5102, name: 'Geelong', stateId: 502 },
  //       { id: 5103, name: 'Ballarat', stateId: 502 },
  //       { id: 5104, name: 'Bendigo', stateId: 502 },
  //       { id: 5105, name: 'Shepparton', stateId: 502 },
  //       { id: 5106, name: 'Mildura', stateId: 502 }
  //     ],
  //     // Canada
  //     601: [ // Ontario
  //       { id: 6001, name: 'Toronto', stateId: 601 },
  //       { id: 6002, name: 'Ottawa', stateId: 601 },
  //       { id: 6003, name: 'Mississauga', stateId: 601 },
  //       { id: 6004, name: 'Hamilton', stateId: 601 },
  //       { id: 6005, name: 'London', stateId: 601 },
  //       { id: 6006, name: 'Windsor', stateId: 601 }
  //     ],
  //     602: [ // Quebec
  //       { id: 6101, name: 'Montreal', stateId: 602 },
  //       { id: 6102, name: 'Quebec City', stateId: 602 },
  //       { id: 6103, name: 'Laval', stateId: 602 },
  //       { id: 6104, name: 'Gatineau', stateId: 602 },
  //       { id: 6105, name: 'Sherbrooke', stateId: 602 },
  //       { id: 6106, name: 'Trois-Rivières', stateId: 602 }
  //     ],
  //     // China
  //     701: [ // Beijing
  //       { id: 7001, name: 'Dongcheng', stateId: 701 },
  //       { id: 7002, name: 'Xicheng', stateId: 701 },
  //       { id: 7003, name: 'Chaoyang', stateId: 701 },
  //       { id: 7004, name: 'Haidian', stateId: 701 },
  //       { id: 7005, name: 'Fengtai', stateId: 701 }
  //     ],
  //     702: [ // Shanghai
  //       { id: 7101, name: 'Pudong', stateId: 702 },
  //       { id: 7102, name: 'Huangpu', stateId: 702 },
  //       { id: 7103, name: 'Jing\'an', stateId: 702 },
  //       { id: 7104, name: 'Xuhui', stateId: 702 },
  //       { id: 7105, name: 'Hongkou', stateId: 702 }
  //     ],
  //     // India
  //     801: [ // Maharashtra
  //       { id: 8001, name: 'Mumbai', stateId: 801 },
  //       { id: 8002, name: 'Pune', stateId: 801 },
  //       { id: 8003, name: 'Nagpur', stateId: 801 },
  //       { id: 8004, name: 'Thane', stateId: 801 },
  //       { id: 8005, name: 'Nashik', stateId: 801 }
  //     ],
  //     802: [ // Tamil Nadu
  //       { id: 8101, name: 'Chennai', stateId: 802 },
  //       { id: 8102, name: 'Coimbatore', stateId: 802 },
  //       { id: 8103, name: 'Madurai', stateId: 802 },
  //       { id: 8104, name: 'Tiruchirappalli', stateId: 802 },
  //       { id: 8105, name: 'Salem', stateId: 802 }
  //     ],
  //     // Japan
  //     901: [ // Tokyo
  //       { id: 9001, name: 'Shinjuku', stateId: 901 },
  //       { id: 9002, name: 'Shibuya', stateId: 901 },
  //       { id: 9003, name: 'Minato', stateId: 901 },
  //       { id: 9004, name: 'Chiyoda', stateId: 901 },
  //       { id: 9005, name: 'Edogawa', stateId: 901 }
  //     ],
  //     902: [ // Osaka
  //       { id: 9101, name: 'Osaka City', stateId: 902 },
  //       { id: 9102, name: 'Sakai', stateId: 902 },
  //       { id: 9103, name: 'Higashiosaka', stateId: 902 },
  //       { id: 9104, name: 'Takatsuki', stateId: 902 },
  //       { id: 9105, name: 'Toyonaka', stateId: 902 }
  //     ],
  //     // United States - Additional states
  //     305: [ // Illinois
  //       { id: 3401, name: 'Chicago', stateId: 305 },
  //       { id: 3402, name: 'Aurora', stateId: 305 },
  //       { id: 3403, name: 'Naperville', stateId: 305 },
  //       { id: 3404, name: 'Peoria', stateId: 305 },
  //       { id: 3405, name: 'Springfield', stateId: 305 }
  //     ],
  //     306: [ // Pennsylvania
  //       { id: 3501, name: 'Philadelphia', stateId: 306 },
  //       { id: 3502, name: 'Pittsburgh', stateId: 306 },
  //       { id: 3503, name: 'Allentown', stateId: 306 },
  //       { id: 3504, name: 'Erie', stateId: 306 },
  //       { id: 3505, name: 'Reading', stateId: 306 }
  //     ],
  //     307: [ // Ohio
  //       { id: 3601, name: 'Columbus', stateId: 307 },
  //       { id: 3602, name: 'Cleveland', stateId: 307 },
  //       { id: 3603, name: 'Cincinnati', stateId: 307 },
  //       { id: 3604, name: 'Toledo', stateId: 307 },
  //       { id: 3605, name: 'Akron', stateId: 307 }
  //     ],
  //     308: [ // Georgia
  //       { id: 3701, name: 'Atlanta', stateId: 308 },
  //       { id: 3702, name: 'Augusta', stateId: 308 },
  //       { id: 3703, name: 'Columbus', stateId: 308 },
  //       { id: 3704, name: 'Savannah', stateId: 308 },
  //       { id: 3705, name: 'Athens', stateId: 308 }
  //     ],
  //     309: [ // North Carolina
  //       { id: 3801, name: 'Charlotte', stateId: 309 },
  //       { id: 3802, name: 'Raleigh', stateId: 309 },
  //       { id: 3803, name: 'Greensboro', stateId: 309 },
  //       { id: 3804, name: 'Durham', stateId: 309 },
  //       { id: 3805, name: 'Winston-Salem', stateId: 309 }
  //     ],
  //     310: [ // Michigan
  //       { id: 3901, name: 'Detroit', stateId: 310 },
  //       { id: 3902, name: 'Grand Rapids', stateId: 310 },
  //       { id: 3903, name: 'Warren', stateId: 310 },
  //       { id: 3904, name: 'Sterling Heights', stateId: 310 },
  //       { id: 3905, name: 'Ann Arbor', stateId: 310 }
  //     ],
  //     311: [ // New Jersey
  //       { id: 4401, name: 'Newark', stateId: 311 },
  //       { id: 4402, name: 'Jersey City', stateId: 311 },
  //       { id: 4403, name: 'Paterson', stateId: 311 },
  //       { id: 4404, name: 'Elizabeth', stateId: 311 },
  //       { id: 4405, name: 'Trenton', stateId: 311 }
  //     ],
  //     312: [ // Virginia
  //       { id: 4501, name: 'Virginia Beach', stateId: 312 },
  //       { id: 4502, name: 'Norfolk', stateId: 312 },
  //       { id: 4503, name: 'Chesapeake', stateId: 312 },
  //       { id: 4504, name: 'Richmond', stateId: 312 },
  //       { id: 4505, name: 'Arlington', stateId: 312 }
  //     ],
  //     313: [ // Washington
  //       { id: 4601, name: 'Seattle', stateId: 313 },
  //       { id: 4602, name: 'Spokane', stateId: 313 },
  //       { id: 4603, name: 'Tacoma', stateId: 313 },
  //       { id: 4604, name: 'Vancouver', stateId: 313 },
  //       { id: 4605, name: 'Bellevue', stateId: 313 }
  //     ],
  //     314: [ // Massachusetts
  //       { id: 4701, name: 'Boston', stateId: 314 },
  //       { id: 4702, name: 'Worcester', stateId: 314 },
  //       { id: 4703, name: 'Springfield', stateId: 314 },
  //       { id: 4704, name: 'Cambridge', stateId: 314 },
  //       { id: 4705, name: 'Lowell', stateId: 314 }
  //     ],
  //     315: [ // Arizona
  //       { id: 4801, name: 'Phoenix', stateId: 315 },
  //       { id: 4802, name: 'Tucson', stateId: 315 },
  //       { id: 4803, name: 'Mesa', stateId: 315 },
  //       { id: 4804, name: 'Chandler', stateId: 315 },
  //       { id: 4805, name: 'Scottsdale', stateId: 315 }
  //     ],
  //     316: [ // Tennessee
  //       { id: 4901, name: 'Nashville', stateId: 316 },
  //       { id: 4902, name: 'Memphis', stateId: 316 },
  //       { id: 4903, name: 'Knoxville', stateId: 316 },
  //       { id: 4904, name: 'Chattanooga', stateId: 316 },
  //       { id: 4905, name: 'Clarksville', stateId: 316 }
  //     ],
  //     317: [ // Indiana
  //       { id: 5801, name: 'Indianapolis', stateId: 317 },
  //       { id: 5802, name: 'Fort Wayne', stateId: 317 },
  //       { id: 5803, name: 'Evansville', stateId: 317 },
  //       { id: 5804, name: 'South Bend', stateId: 317 },
  //       { id: 5805, name: 'Carmel', stateId: 317 }
  //     ],
  //     318: [ // Missouri
  //       { id: 5901, name: 'Kansas City', stateId: 318 },
  //       { id: 5902, name: 'St. Louis', stateId: 318 },
  //       { id: 5903, name: 'Springfield', stateId: 318 },
  //       { id: 5904, name: 'Columbia', stateId: 318 },
  //       { id: 5905, name: 'Independence', stateId: 318 }
  //     ],
  //     319: [ // Maryland
  //       { id: 6001, name: 'Baltimore', stateId: 319 },
  //       { id: 6002, name: 'Frederick', stateId: 319 },
  //       { id: 6003, name: 'Rockville', stateId: 319 },
  //       { id: 6004, name: 'Gaithersburg', stateId: 319 },
  //       { id: 6005, name: 'Bowie', stateId: 319 }
  //     ],
  //     320: [ // Wisconsin
  //       { id: 6101, name: 'Milwaukee', stateId: 320 },
  //       { id: 6102, name: 'Madison', stateId: 320 },
  //       { id: 6103, name: 'Green Bay', stateId: 320 },
  //       { id: 6104, name: 'Kenosha', stateId: 320 },
  //       { id: 6105, name: 'Racine', stateId: 320 }
  //     ],
  //     // Singapore regions
  //     201: [ // Central Region
  //       { id: 2001, name: 'Downtown Core', stateId: 201 },
  //       { id: 2002, name: 'Orchard', stateId: 201 },
  //       { id: 2003, name: 'Marina Bay', stateId: 201 },
  //       { id: 2004, name: 'Raffles Place', stateId: 201 },
  //       { id: 2005, name: 'Tanjong Pagar', stateId: 201 }
  //     ],
  //     202: [ // East Region
  //       { id: 2101, name: 'Changi', stateId: 202 },
  //       { id: 2102, name: 'Bedok', stateId: 202 },
  //       { id: 2103, name: 'Tampines', stateId: 202 },
  //       { id: 2104, name: 'Pasir Ris', stateId: 202 },
  //       { id: 2105, name: 'East Coast', stateId: 202 }
  //     ],
  //     203: [ // North Region
  //       { id: 2201, name: 'Woodlands', stateId: 203 },
  //       { id: 2202, name: 'Sembawang', stateId: 203 },
  //       { id: 2203, name: 'Yishun', stateId: 203 },
  //       { id: 2204, name: 'Mandai', stateId: 203 },
  //       { id: 2205, name: 'Admiralty', stateId: 203 }
  //     ],
  //     204: [ // North-East Region
  //       { id: 2301, name: 'Sengkang', stateId: 204 },
  //       { id: 2302, name: 'Punggol', stateId: 204 },
  //       { id: 2303, name: 'Hougang', stateId: 204 },
  //       { id: 2304, name: 'Serangoon', stateId: 204 },
  //       { id: 2305, name: 'Ang Mo Kio', stateId: 204 }
  //     ],
  //     205: [ // West Region
  //       { id: 2401, name: 'Jurong East', stateId: 205 },
  //       { id: 2402, name: 'Jurong West', stateId: 205 },
  //       { id: 2403, name: 'Clementi', stateId: 205 },
  //       { id: 2404, name: 'Bukit Batok', stateId: 205 },
  //       { id: 2405, name: 'Choa Chu Kang', stateId: 205 }
  //     ],
  //     // More United States states
  //     301: [ // California
  //       { id: 3001, name: 'Los Angeles', stateId: 301 },
  //       { id: 3002, name: 'San Francisco', stateId: 301 },
  //       { id: 3003, name: 'San Diego', stateId: 301 },
  //       { id: 3004, name: 'Sacramento', stateId: 301 },
  //       { id: 3005, name: 'San Jose', stateId: 301 }
  //     ],
  //     302: [ // New York
  //       { id: 3101, name: 'New York City', stateId: 302 },
  //       { id: 3102, name: 'Buffalo', stateId: 302 },
  //       { id: 3103, name: 'Rochester', stateId: 302 },
  //       { id: 3104, name: 'Syracuse', stateId: 302 },
  //       { id: 3105, name: 'Albany', stateId: 302 }
  //     ],
  //     303: [ // Texas
  //       { id: 3201, name: 'Houston', stateId: 303 },
  //       { id: 3202, name: 'Dallas', stateId: 303 },
  //       { id: 3203, name: 'Austin', stateId: 303 },
  //       { id: 3204, name: 'San Antonio', stateId: 303 },
  //       { id: 3205, name: 'Fort Worth', stateId: 303 }
  //     ],
  //     304: [ // Florida
  //       { id: 3301, name: 'Miami', stateId: 304 },
  //       { id: 3302, name: 'Orlando', stateId: 304 },
  //       { id: 3303, name: 'Tampa', stateId: 304 },
  //       { id: 3304, name: 'Jacksonville', stateId: 304 },
  //       { id: 3305, name: 'Fort Lauderdale', stateId: 304 }
  //     ],
  //     // France (Country ID: 11)
  //     1101: [ // Île-de-France
  //       { id: 11001, name: 'Paris', stateId: 1101 },
  //       { id: 11002, name: 'Versailles', stateId: 1101 },
  //       { id: 11003, name: 'Saint-Denis', stateId: 1101 },
  //       { id: 11004, name: 'Boulogne-Billancourt', stateId: 1101 },
  //       { id: 11005, name: 'Argenteuil', stateId: 1101 }
  //     ],
  //     1102: [ // Provence-Alpes-Côte d'Azur
  //       { id: 11101, name: 'Marseille', stateId: 1102 },
  //       { id: 11102, name: 'Nice', stateId: 1102 },
  //       { id: 11103, name: 'Toulon', stateId: 1102 },
  //       { id: 11104, name: 'Aix-en-Provence', stateId: 1102 },
  //       { id: 11105, name: 'Avignon', stateId: 1102 }
  //     ],
  //     1103: [ // Nouvelle-Aquitaine
  //       { id: 11201, name: 'Bordeaux', stateId: 1103 },
  //       { id: 11202, name: 'Limoges', stateId: 1103 },
  //       { id: 11203, name: 'Poitiers', stateId: 1103 },
  //       { id: 11204, name: 'La Rochelle', stateId: 1103 },
  //       { id: 11205, name: 'Bayonne', stateId: 1103 }
  //     ],
  //     1104: [ // Auvergne-Rhône-Alpes
  //       { id: 11301, name: 'Lyon', stateId: 1104 },
  //       { id: 11302, name: 'Grenoble', stateId: 1104 },
  //       { id: 11303, name: 'Saint-Étienne', stateId: 1104 },
  //       { id: 11304, name: 'Clermont-Ferrand', stateId: 1104 },
  //       { id: 11305, name: 'Annecy', stateId: 1104 }
  //     ],
  //     1105: [ // Occitanie
  //       { id: 11401, name: 'Toulouse', stateId: 1105 },
  //       { id: 11402, name: 'Montpellier', stateId: 1105 },
  //       { id: 11403, name: 'Nîmes', stateId: 1105 },
  //       { id: 11404, name: 'Perpignan', stateId: 1105 },
  //       { id: 11405, name: 'Béziers', stateId: 1105 }
  //     ],
  //     // Italy (Country ID: 12)
  //     1201: [ // Lombardy
  //       { id: 12001, name: 'Milan', stateId: 1201 },
  //       { id: 12002, name: 'Brescia', stateId: 1201 },
  //       { id: 12003, name: 'Bergamo', stateId: 1201 },
  //       { id: 12004, name: 'Monza', stateId: 1201 },
  //       { id: 12005, name: 'Como', stateId: 1201 }
  //     ],
  //     1202: [ // Lazio
  //       { id: 12101, name: 'Rome', stateId: 1202 },
  //       { id: 12102, name: 'Latina', stateId: 1202 },
  //       { id: 12103, name: 'Viterbo', stateId: 1202 },
  //       { id: 12104, name: 'Frosinone', stateId: 1202 },
  //       { id: 12105, name: 'Rieti', stateId: 1202 }
  //     ],
  //     1203: [ // Campania
  //       { id: 12201, name: 'Naples', stateId: 1203 },
  //       { id: 12202, name: 'Salerno', stateId: 1203 },
  //       { id: 12203, name: 'Caserta', stateId: 1203 },
  //       { id: 12204, name: 'Benevento', stateId: 1203 },
  //       { id: 12205, name: 'Avellino', stateId: 1203 }
  //     ],
  //     1204: [ // Sicily
  //       { id: 12301, name: 'Palermo', stateId: 1204 },
  //       { id: 12302, name: 'Catania', stateId: 1204 },
  //       { id: 12303, name: 'Messina', stateId: 1204 },
  //       { id: 12304, name: 'Syracuse', stateId: 1204 },
  //       { id: 12305, name: 'Ragusa', stateId: 1204 }
  //     ],
  //     1205: [ // Veneto
  //       { id: 12401, name: 'Venice', stateId: 1205 },
  //       { id: 12402, name: 'Verona', stateId: 1205 },
  //       { id: 12403, name: 'Padua', stateId: 1205 },
  //       { id: 12404, name: 'Vicenza', stateId: 1205 },
  //       { id: 12405, name: 'Treviso', stateId: 1205 }
  //     ],
  //     // Brazil (Country ID: 13)
  //     1301: [ // São Paulo
  //       { id: 13001, name: 'São Paulo', stateId: 1301 },
  //       { id: 13002, name: 'Campinas', stateId: 1301 },
  //       { id: 13003, name: 'Santos', stateId: 1301 },
  //       { id: 13004, name: 'Ribeirão Preto', stateId: 1301 },
  //       { id: 13005, name: 'São José dos Campos', stateId: 1301 }
  //     ],
  //     1302: [ // Rio de Janeiro
  //       { id: 13101, name: 'Rio de Janeiro', stateId: 1302 },
  //       { id: 13102, name: 'Niterói', stateId: 1302 },
  //       { id: 13103, name: 'Duque de Caxias', stateId: 1302 },
  //       { id: 13104, name: 'Nova Iguaçu', stateId: 1302 },
  //       { id: 13105, name: 'Petrópolis', stateId: 1302 }
  //     ],
  //     1303: [ // Minas Gerais
  //       { id: 13201, name: 'Belo Horizonte', stateId: 1303 },
  //       { id: 13202, name: 'Uberlândia', stateId: 1303 },
  //       { id: 13203, name: 'Contagem', stateId: 1303 },
  //       { id: 13204, name: 'Juiz de Fora', stateId: 1303 },
  //       { id: 13205, name: 'Betim', stateId: 1303 }
  //     ],
  //     1304: [ // Bahia
  //       { id: 13301, name: 'Salvador', stateId: 1304 },
  //       { id: 13302, name: 'Feira de Santana', stateId: 1304 },
  //       { id: 13303, name: 'Vitória da Conquista', stateId: 1304 },
  //       { id: 13304, name: 'Camaçari', stateId: 1304 },
  //       { id: 13305, name: 'Itabuna', stateId: 1304 }
  //     ],
  //     1305: [ // Rio Grande do Sul
  //       { id: 13401, name: 'Porto Alegre', stateId: 1305 },
  //       { id: 13402, name: 'Caxias do Sul', stateId: 1305 },
  //       { id: 13403, name: 'Pelotas', stateId: 1305 },
  //       { id: 13404, name: 'Canoas', stateId: 1305 },
  //       { id: 13405, name: 'Santa Maria', stateId: 1305 }
  //     ],
  //     // South Korea (Country ID: 14)
  //     1401: [ // Seoul
  //       { id: 14001, name: 'Gangnam District', stateId: 1401 },
  //       { id: 14002, name: 'Mapo District', stateId: 1401 },
  //       { id: 14003, name: 'Jongno District', stateId: 1401 },
  //       { id: 14004, name: 'Yongsan District', stateId: 1401 },
  //       { id: 14005, name: 'Songpa District', stateId: 1401 }
  //     ],
  //     1402: [ // Busan
  //       { id: 14101, name: 'Haeundae District', stateId: 1402 },
  //       { id: 14102, name: 'Suyeong District', stateId: 1402 },
  //       { id: 14103, name: 'Busanjin District', stateId: 1402 },
  //       { id: 14104, name: 'Geumjeong District', stateId: 1402 },
  //       { id: 14105, name: 'Nam District', stateId: 1402 }
  //     ],
  //     1403: [ // Incheon
  //       { id: 14201, name: 'Jung District', stateId: 1403 },
  //       { id: 14202, name: 'Yeonsu District', stateId: 1403 },
  //       { id: 14203, name: 'Namdong District', stateId: 1403 },
  //       { id: 14204, name: 'Bupyeong District', stateId: 1403 },
  //       { id: 14205, name: 'Seo District', stateId: 1403 }
  //     ],
  //     // Indonesia (Country ID: 15)
  //     1501: [ // Jakarta
  //       { id: 15001, name: 'Central Jakarta', stateId: 1501 },
  //       { id: 15002, name: 'South Jakarta', stateId: 1501 },
  //       { id: 15003, name: 'North Jakarta', stateId: 1501 },
  //       { id: 15004, name: 'East Jakarta', stateId: 1501 },
  //       { id: 15005, name: 'West Jakarta', stateId: 1501 }
  //     ],
  //     1502: [ // West Java
  //       { id: 15101, name: 'Bandung', stateId: 1502 },
  //       { id: 15102, name: 'Bekasi', stateId: 1502 },
  //       { id: 15103, name: 'Depok', stateId: 1502 },
  //       { id: 15104, name: 'Bogor', stateId: 1502 },
  //       { id: 15105, name: 'Cimahi', stateId: 1502 }
  //     ],
  //     1503: [ // East Java
  //       { id: 15201, name: 'Surabaya', stateId: 1503 },
  //       { id: 15202, name: 'Malang', stateId: 1503 },
  //       { id: 15203, name: 'Kediri', stateId: 1503 },
  //       { id: 15204, name: 'Jember', stateId: 1503 },
  //       { id: 15205, name: 'Probolinggo', stateId: 1503 }
  //     ],
  //     // Thailand (Country ID: 16)
  //     1601: [ // Bangkok
  //       { id: 16001, name: 'Watthana', stateId: 1601 },
  //       { id: 16002, name: 'Pathum Wan', stateId: 1601 },
  //       { id: 16003, name: 'Sathon', stateId: 1601 },
  //       { id: 16004, name: 'Bang Rak', stateId: 1601 },
  //       { id: 16005, name: 'Khlong Toei', stateId: 1601 }
  //     ],
  //     1602: [ // Chiang Mai
  //       { id: 16101, name: 'Mueang Chiang Mai', stateId: 1602 },
  //       { id: 16102, name: 'Hang Dong', stateId: 1602 },
  //       { id: 16103, name: 'San Sai', stateId: 1602 },
  //       { id: 16104, name: 'Mae Rim', stateId: 1602 },
  //       { id: 16105, name: 'San Kamphaeng', stateId: 1602 }
  //     ],
  //     1603: [ // Phuket
  //       { id: 16201, name: 'Mueang Phuket', stateId: 1603 },
  //       { id: 16202, name: 'Kathu', stateId: 1603 },
  //       { id: 16203, name: 'Thalang', stateId: 1603 },
  //       { id: 16204, name: 'Patong', stateId: 1603 },
  //       { id: 16205, name: 'Karon', stateId: 1603 }
  //     ],
  //     // Vietnam (Country ID: 17)
  //     1701: [ // Ho Chi Minh City
  //       { id: 17001, name: 'District 1', stateId: 1701 },
  //       { id: 17002, name: 'District 2', stateId: 1701 },
  //       { id: 17003, name: 'District 3', stateId: 1701 },
  //       { id: 17004, name: 'District 4', stateId: 1701 },
  //       { id: 17005, name: 'District 5', stateId: 1701 }
  //     ],
  //     1702: [ // Hanoi
  //       { id: 17101, name: 'Hoan Kiem', stateId: 1702 },
  //       { id: 17102, name: 'Ba Dinh', stateId: 1702 },
  //       { id: 17103, name: 'Dong Da', stateId: 1702 },
  //       { id: 17104, name: 'Hai Ba Trung', stateId: 1702 },
  //       { id: 17105, name: 'Tay Ho', stateId: 1702 }
  //     ],
  //     1703: [ // Da Nang
  //       { id: 17201, name: 'Hai Chau', stateId: 1703 },
  //       { id: 17202, name: 'Thanh Khe', stateId: 1703 },
  //       { id: 17203, name: 'Son Tra', stateId: 1703 },
  //       { id: 17204, name: 'Ngu Hanh Son', stateId: 1703 },
  //       { id: 17205, name: 'Lien Chieu', stateId: 1703 }
  //     ],
  //     // Philippines (Country ID: 18)
  //     1801: [ // Metro Manila
  //       { id: 18001, name: 'Manila', stateId: 1801 },
  //       { id: 18002, name: 'Quezon City', stateId: 1801 },
  //       { id: 18003, name: 'Makati', stateId: 1801 },
  //       { id: 18004, name: 'Taguig', stateId: 1801 },
  //       { id: 18005, name: 'Pasig', stateId: 1801 }
  //     ],
  //     1802: [ // Cebu
  //       { id: 18101, name: 'Cebu City', stateId: 1802 },
  //       { id: 18102, name: 'Mandaue', stateId: 1802 },
  //       { id: 18103, name: 'Lapu-Lapu', stateId: 1802 },
  //       { id: 18104, name: 'Talisay', stateId: 1802 },
  //       { id: 18105, name: 'Danao', stateId: 1802 }
  //     ],
  //     1803: [ // Davao
  //       { id: 18201, name: 'Davao City', stateId: 1803 },
  //       { id: 18202, name: 'Tagum', stateId: 1803 },
  //       { id: 18203, name: 'Panabo', stateId: 1803 },
  //       { id: 18204, name: 'Digos', stateId: 1803 },
  //       { id: 18205, name: 'Mati', stateId: 1803 }
  //     ],
  //     // Spain (Country ID: 19)
  //     1901: [ // Madrid
  //       { id: 19001, name: 'Madrid City', stateId: 1901 },
  //       { id: 19002, name: 'Móstoles', stateId: 1901 },
  //       { id: 19003, name: 'Alcalá de Henares', stateId: 1901 },
  //       { id: 19004, name: 'Fuenlabrada', stateId: 1901 },
  //       { id: 19005, name: 'Leganés', stateId: 1901 }
  //     ],
  //     1902: [ // Catalonia
  //       { id: 19101, name: 'Barcelona', stateId: 1902 },
  //       { id: 19102, name: 'L\'Hospitalet', stateId: 1902 },
  //       { id: 19103, name: 'Terrassa', stateId: 1902 },
  //       { id: 19104, name: 'Sabadell', stateId: 1902 },
  //       { id: 19105, name: 'Tarragona', stateId: 1902 }
  //     ],
  //     1903: [ // Andalusia
  //       { id: 19201, name: 'Seville', stateId: 1903 },
  //       { id: 19202, name: 'Málaga', stateId: 1903 },
  //       { id: 19203, name: 'Córdoba', stateId: 1903 },
  //       { id: 19204, name: 'Granada', stateId: 1903 },
  //       { id: 19205, name: 'Cádiz', stateId: 1903 }
  //     ],
  //     // Netherlands (Country ID: 20)
  //     2001: [ // North Holland
  //       { id: 20001, name: 'Amsterdam', stateId: 2001 },
  //       { id: 20002, name: 'Haarlem', stateId: 2001 },
  //       { id: 20003, name: 'Zaanstad', stateId: 2001 },
  //       { id: 20004, name: 'Haarlemmermeer', stateId: 2001 },
  //       { id: 20005, name: 'Alkmaar', stateId: 2001 }
  //     ],
  //     2002: [ // South Holland
  //       { id: 20101, name: 'Rotterdam', stateId: 2002 },
  //       { id: 20102, name: 'The Hague', stateId: 2002 },
  //       { id: 20103, name: 'Leiden', stateId: 2002 },
  //       { id: 20104, name: 'Dordrecht', stateId: 2002 },
  //       { id: 20105, name: 'Delft', stateId: 2002 }
  //     ],
  //     2003: [ // Utrecht
  //       { id: 20201, name: 'Utrecht City', stateId: 2003 },
  //       { id: 20202, name: 'Amersfoort', stateId: 2003 },
  //       { id: 20203, name: 'Veenendaal', stateId: 2003 },
  //       { id: 20204, name: 'Zeist', stateId: 2003 },
  //       { id: 20205, name: 'Nieuwegein', stateId: 2003 }
  //     ],
  //     // Mexico (Country ID: 21)
  //     2101: [ // Mexico City
  //       { id: 21001, name: 'Cuauhtémoc', stateId: 2101 },
  //       { id: 21002, name: 'Miguel Hidalgo', stateId: 2101 },
  //       { id: 21003, name: 'Benito Juárez', stateId: 2101 },
  //       { id: 21004, name: 'Coyoacán', stateId: 2101 },
  //       { id: 21005, name: 'Tlalpan', stateId: 2101 }
  //     ],
  //     2102: [ // Jalisco
  //       { id: 21101, name: 'Guadalajara', stateId: 2102 },
  //       { id: 21102, name: 'Zapopan', stateId: 2102 },
  //       { id: 21103, name: 'Tlaquepaque', stateId: 2102 },
  //       { id: 21104, name: 'Tonalá', stateId: 2102 },
  //       { id: 21105, name: 'Puerto Vallarta', stateId: 2102 }
  //     ],
  //     2103: [ // Nuevo León
  //       { id: 21201, name: 'Monterrey', stateId: 2103 },
  //       { id: 21202, name: 'Guadalupe', stateId: 2103 },
  //       { id: 21203, name: 'San Nicolás de los Garza', stateId: 2103 },
  //       { id: 21204, name: 'Apodaca', stateId: 2103 },
  //       { id: 21205, name: 'Santa Catarina', stateId: 2103 }
  //     ],
  //     // Turkey (Country ID: 22)
  //     2201: [ // Istanbul
  //       { id: 22001, name: 'Fatih', stateId: 2201 },
  //       { id: 22002, name: 'Beyoğlu', stateId: 2201 },
  //       { id: 22003, name: 'Kadıköy', stateId: 2201 },
  //       { id: 22004, name: 'Üsküdar', stateId: 2201 },
  //       { id: 22005, name: 'Beşiktaş', stateId: 2201 }
  //     ],
  //     2202: [ // Ankara
  //       { id: 22101, name: 'Çankaya', stateId: 2202 },
  //       { id: 22102, name: 'Keçiören', stateId: 2202 },
  //       { id: 22103, name: 'Yenimahalle', stateId: 2202 },
  //       { id: 22104, name: 'Mamak', stateId: 2202 },
  //       { id: 22105, name: 'Altındağ', stateId: 2202 }
  //     ],
  //     2203: [ // Izmir
  //       { id: 22201, name: 'Konak', stateId: 2203 },
  //       { id: 22202, name: 'Karşıyaka', stateId: 2203 },
  //       { id: 22203, name: 'Bornova', stateId: 2203 },
  //       { id: 22204, name: 'Buca', stateId: 2203 },
  //       { id: 22205, name: 'Çiğli', stateId: 2203 }
  //     ],
  //     // Switzerland (Country ID: 23)
  //     2301: [ // Zurich
  //       { id: 23001, name: 'Zurich City', stateId: 2301 },
  //       { id: 23002, name: 'Winterthur', stateId: 2301 },
  //       { id: 23003, name: 'Uster', stateId: 2301 },
  //       { id: 23004, name: 'Dübendorf', stateId: 2301 },
  //       { id: 23005, name: 'Dietikon', stateId: 2301 }
  //     ],
  //     2302: [ // Geneva
  //       { id: 23101, name: 'Geneva City', stateId: 2302 },
  //       { id: 23102, name: 'Vernier', stateId: 2302 },
  //       { id: 23103, name: 'Lancy', stateId: 2302 },
  //       { id: 23104, name: 'Meyrin', stateId: 2302 },
  //       { id: 23105, name: 'Carouge', stateId: 2302 }
  //     ],
  //     2303: [ // Bern
  //       { id: 23201, name: 'Bern City', stateId: 2303 },
  //       { id: 23202, name: 'Biel/Bienne', stateId: 2303 },
  //       { id: 23203, name: 'Thun', stateId: 2303 },
  //       { id: 23204, name: 'Köniz', stateId: 2303 },
  //       { id: 23205, name: 'Burgdorf', stateId: 2303 }
  //     ],
  //     // Sweden (Country ID: 24)
  //     2401: [ // Stockholm
  //       { id: 24001, name: 'Stockholm City', stateId: 2401 },
  //       { id: 24002, name: 'Solna', stateId: 2401 },
  //       { id: 24003, name: 'Sundbyberg', stateId: 2401 },
  //       { id: 24004, name: 'Lidingö', stateId: 2401 },
  //       { id: 24005, name: 'Nacka', stateId: 2401 }
  //     ],
  //     2402: [ // Västra Götaland
  //       { id: 24101, name: 'Gothenburg', stateId: 2402 },
  //       { id: 24102, name: 'Borås', stateId: 2402 },
  //       { id: 24103, name: 'Trollhättan', stateId: 2402 },
  //       { id: 24104, name: 'Skövde', stateId: 2402 },
  //       { id: 24105, name: 'Uddevalla', stateId: 2402 }
  //     ],
  //     2403: [ // Skåne
  //       { id: 24201, name: 'Malmö', stateId: 2403 },
  //       { id: 24202, name: 'Helsingborg', stateId: 2403 },
  //       { id: 24203, name: 'Lund', stateId: 2403 },
  //       { id: 24204, name: 'Kristianstad', stateId: 2403 },
  //       { id: 24205, name: 'Landskrona', stateId: 2403 }
  //     ],
  //     // New Zealand (Country ID: 25)
  //     2501: [ // Auckland
  //       { id: 25001, name: 'Auckland Central', stateId: 2501 },
  //       { id: 25002, name: 'North Shore', stateId: 2501 },
  //       { id: 25003, name: 'Manukau', stateId: 2501 },
  //       { id: 25004, name: 'Waitakere', stateId: 2501 },
  //       { id: 25005, name: 'Papakura', stateId: 2501 }
  //     ],
  //     2502: [ // Wellington
  //       { id: 25101, name: 'Wellington City', stateId: 2502 },
  //       { id: 25102, name: 'Lower Hutt', stateId: 2502 },
  //       { id: 25103, name: 'Upper Hutt', stateId: 2502 },
  //       { id: 25104, name: 'Porirua', stateId: 2502 },
  //       { id: 25105, name: 'Kapiti Coast', stateId: 2502 }
  //     ],
  //     2503: [ // Canterbury
  //       { id: 25201, name: 'Christchurch', stateId: 2503 },
  //       { id: 25202, name: 'Timaru', stateId: 2503 },
  //       { id: 25203, name: 'Ashburton', stateId: 2503 },
  //       { id: 25204, name: 'Rangiora', stateId: 2503 },
  //       { id: 25205, name: 'Rolleston', stateId: 2503 }
  //     ],
  //     // Ireland (Country ID: 26)
  //     2601: [ // Dublin
  //       { id: 26001, name: 'Dublin City', stateId: 2601 },
  //       { id: 26002, name: 'Dún Laoghaire', stateId: 2601 },
  //       { id: 26003, name: 'Tallaght', stateId: 2601 },
  //       { id: 26004, name: 'Swords', stateId: 2601 },
  //       { id: 26005, name: 'Lucan', stateId: 2601 }
  //     ],
  //     2602: [ // Cork
  //       { id: 26101, name: 'Cork City', stateId: 2602 },
  //       { id: 26102, name: 'Carrigaline', stateId: 2602 },
  //       { id: 26103, name: 'Cobh', stateId: 2602 },
  //       { id: 26104, name: 'Midleton', stateId: 2602 },
  //       { id: 26105, name: 'Mallow', stateId: 2602 }
  //     ],
  //     2603: [ // Galway
  //       { id: 26201, name: 'Galway City', stateId: 2603 },
  //       { id: 26202, name: 'Tuam', stateId: 2603 },
  //       { id: 26203, name: 'Ballinasloe', stateId: 2603 },
  //       { id: 26204, name: 'Loughrea', stateId: 2603 },
  //       { id: 26205, name: 'Athenry', stateId: 2603 }
  //     ],
  //     // Belgium (Country ID: 27)
  //     2701: [ // Brussels
  //       { id: 27001, name: 'Brussels City', stateId: 2701 },
  //       { id: 27002, name: 'Schaerbeek', stateId: 2701 },
  //       { id: 27003, name: 'Anderlecht', stateId: 2701 },
  //       { id: 27004, name: 'Molenbeek', stateId: 2701 },
  //       { id: 27005, name: 'Ixelles', stateId: 2701 }
  //     ],
  //     2702: [ // Flanders
  //       { id: 27101, name: 'Antwerp', stateId: 2702 },
  //       { id: 27102, name: 'Ghent', stateId: 2702 },
  //       { id: 27103, name: 'Bruges', stateId: 2702 },
  //       { id: 27104, name: 'Leuven', stateId: 2702 },
  //       { id: 27105, name: 'Mechelen', stateId: 2702 }
  //     ],
  //     2703: [ // Wallonia
  //       { id: 27201, name: 'Liège', stateId: 2703 },
  //       { id: 27202, name: 'Charleroi', stateId: 2703 },
  //       { id: 27203, name: 'Namur', stateId: 2703 },
  //       { id: 27204, name: 'Mons', stateId: 2703 },
  //       { id: 27205, name: 'Tournai', stateId: 2703 }
  //     ],
  //     // Norway (Country ID: 28)
  //     2801: [ // Oslo
  //       { id: 28001, name: 'Oslo Central', stateId: 2801 },
  //       { id: 28002, name: 'Frogner', stateId: 2801 },
  //       { id: 28003, name: 'Grünerløkka', stateId: 2801 },
  //       { id: 28004, name: 'St. Hanshaugen', stateId: 2801 },
  //       { id: 28005, name: 'Sagene', stateId: 2801 }
  //     ],
  //     2802: [ // Vestland
  //       { id: 28101, name: 'Bergen', stateId: 2802 },
  //       { id: 28102, name: 'Ålesund', stateId: 2802 },
  //       { id: 28103, name: 'Førde', stateId: 2802 },
  //       { id: 28104, name: 'Sogndal', stateId: 2802 },
  //       { id: 28105, name: 'Stryn', stateId: 2802 }
  //     ],
  //     2803: [ // Trøndelag
  //       { id: 28201, name: 'Trondheim', stateId: 2803 },
  //       { id: 28202, name: 'Steinkjer', stateId: 2803 },
  //       { id: 28203, name: 'Stjørdal', stateId: 2803 },
  //       { id: 28204, name: 'Levanger', stateId: 2803 },
  //       { id: 28205, name: 'Verdal', stateId: 2803 }
  //     ],
  //     // Denmark (Country ID: 29)
  //     2901: [ // Capital Region
  //       { id: 29001, name: 'Copenhagen', stateId: 2901 },
  //       { id: 29002, name: 'Frederiksberg', stateId: 2901 },
  //       { id: 29003, name: 'Helsingør', stateId: 2901 },
  //       { id: 29004, name: 'Roskilde', stateId: 2901 },
  //       { id: 29005, name: 'Hillerød', stateId: 2901 }
  //     ],
  //     2902: [ // Central Denmark
  //       { id: 29101, name: 'Aarhus', stateId: 2902 },
  //       { id: 29102, name: 'Randers', stateId: 2902 },
  //       { id: 29103, name: 'Viborg', stateId: 2902 },
  //       { id: 29104, name: 'Silkeborg', stateId: 2902 },
  //       { id: 29105, name: 'Horsens', stateId: 2902 }
  //     ],
  //     2903: [ // Southern Denmark
  //       { id: 29201, name: 'Odense', stateId: 2903 },
  //       { id: 29202, name: 'Esbjerg', stateId: 2903 },
  //       { id: 29203, name: 'Kolding', stateId: 2903 },
  //       { id: 29204, name: 'Vejle', stateId: 2903 },
  //       { id: 29205, name: 'Sønderborg', stateId: 2903 }
  //     ],
  //     // Finland (Country ID: 30)
  //     3001: [ // Uusimaa
  //       { id: 30001, name: 'Helsinki', stateId: 3001 },
  //       { id: 30002, name: 'Espoo', stateId: 3001 },
  //       { id: 30003, name: 'Vantaa', stateId: 3001 },
  //       { id: 30004, name: 'Porvoo', stateId: 3001 },
  //       { id: 30005, name: 'Lohja', stateId: 3001 }
  //     ],
  //     3002: [ // Pirkanmaa
  //       { id: 30101, name: 'Tampere', stateId: 3002 },
  //       { id: 30102, name: 'Nokia', stateId: 3002 },
  //       { id: 30103, name: 'Ylöjärvi', stateId: 3002 },
  //       { id: 30104, name: 'Kangasala', stateId: 3002 },
  //       { id: 30105, name: 'Sastamala', stateId: 3002 }
  //     ],
  //     3003: [ // Southwest Finland
  //       { id: 30201, name: 'Turku', stateId: 3003 },
  //       { id: 30202, name: 'Salo', stateId: 3003 },
  //       { id: 30203, name: 'Raisio', stateId: 3003 },
  //       { id: 30204, name: 'Kaarina', stateId: 3003 },
  //       { id: 30205, name: 'Naantali', stateId: 3003 }
  //     ]
    };

  //   return { data: citiesByState[stateId] || [] };
  // }
}

const _locationService = new LocationService();
const LocationServiceAPI = {
  getAllCountries: (...args) => _locationService.getAllCountries(...args),
  getStatesByCountry: (...args) => _locationService.getStatesByCountry(...args),
  getCitiesByState: (...args) => _locationService.getCitiesByState(...args),
  searchCountries: (...args) => _locationService.searchCountries(...args),
  searchStates: (...args) => _locationService.searchStates(...args),
  searchCities: (...args) => _locationService.searchCities(...args),
  getMockCountries: (...args) => _locationService.getMockCountries(...args),
  getMockStates: (...args) => _locationService.getMockStates(...args),
  getMockCities: (...args) => _locationService.getMockCities(...args),
};
export default LocationServiceAPI;